import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
  createSeriesMarkers,
  type CandlestickData,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type LineData,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { getExchanges, getCandles, getDetailedSpread, type CandleDataPoint, type DetailedSpreadDataPoint } from '@/services/spreadApi';
import { getIndexPriceHistory, type IndexPriceCandle, type IndexPriceExchange } from '@/services/indexPriceApi';
import { HorizontalSplitPane } from '@/components/HorizontalSplitPane';
import { ManualTradingPanel } from '@/components/ManualTradingPanel';
import { TradingEventLog } from '@/components/TradingEventLog';
import { VerticalResizeHandle } from '@/components/VerticalResizeHandle';
import { BasicInfoPanel } from '@/components/BasicInfoPanel';
import { DataDetailPanel } from '@/components/DataDetailPanel';
import { TradeVolumeChartOverlay, TradeVolumeProvider } from '@/components/TradeVolumeStrip';
import { FundingChartOverlay, type FundingInfoStripProps, type FundingInstrumentMeta } from '@/components/FundingInfoStrip';
import { SpreadPairSelector, type SpreadPairSelectorValue } from '@/components/SpreadPairSelector';
import { TradingOverviewBar } from '@/components/TradingOverviewBar';
import type { PositionGroupView } from '@/services/manualTradingApi';
import {
  createOppDetectorWsTicket,
  getOppDetailedSpread,
  getOppFundingHistory,
  getOppInstrumentInfo,
  getOppL1Candles,
  getOppOrderbookExchanges,
  getOppSpreadDeviationCycles,
  getOppTradeCandles,
  getOppTradePriceSpread,
  getRealtimeSpreadWsUrl,
  type OppSpreadPriceDataSource,
  type OppTradePriceSpreadPoint,
  type OppSpreadDeviationCyclesResponse,
  type OppFundingHistoryResponse,
  type OppFundingRateSample,
  type OppResampledFundingPoint,
  OPP_DETECTOR_DEFAULT_EXCHANGE1_FEE,
  OPP_DETECTOR_DEFAULT_EXCHANGE2_FEE,
  OPP_DETECTOR_DEFAULT_MIN_PROFIT_PERCENTAGE,
  defaultOppDeviationCycleConfig,
  type OppDeviationCycleConfig,
  type RealtimeIndexMetricsMessage,
  type RealtimeKlineUpdateMessage,
  type RealtimeSpreadDirection,
  type RealtimeSpreadWsMessage,
} from '@/services/oppDetectorApi';
import {
  formatAdaptivePrice,
  formatCandleTooltipValue,
  formatSpreadFundingValue,
  SPREAD_FUNDING_PRICE_FORMAT,
} from '@/lib/chartFormat';
import { detailedSpreadViewModeLabel, type DetailedSpreadViewMode } from '@/lib/detailedSpreadChartLines';
import { attachChartRangeMeasure, buildNetFundingSettlementLine } from '@/lib/chartRangeMeasure';
import {
  createNetFundingBarSeriesView,
  mapNetFundingToBarData,
  NET_FUNDING_NEGATIVE_COLOR,
  NET_FUNDING_POSITIVE_COLOR,
  NET_FUNDING_ZERO_COLOR,
  type NetFundingBarData,
} from '@/lib/netFundingBarSeries';
import {
  buildInstructionExchangePairQuery,
  clearInstructionThresholdPriceLines,
  filterInstructionsForChartSpreadDirection,
  INSTRUCTIONS_CHANGED_EVENT,
  reapplyInstructionThresholdPriceLines,
  selectActiveInstructionThresholds,
  type InstructionThresholdLine,
} from '@/lib/instructionThresholdLines';
import {
  reapplyPositionOpenSpreadPriceLine,
  clearPositionOpenSpreadPriceLine,
  findMatchingPositionGroupForChart,
  resolvePositionOpenSpreadChartLine,
  type PositionOpenSpreadChartLine,
} from '@/lib/positionOpenSpreadLine';
import {
  getLatestChartSeriesRawValue,
  type LatestOpenLineByDirection,
} from '@/lib/openLineThresholdConfirm';
import { useAuth } from '@/auth/AuthContext';
import { createSerialLoader } from '@/lib/instructionPolling';
import {
  isManualTradingConfigured,
  listInstructionsIfIdle,
  type InstructionView,
} from '@/services/manualTradingApi';
import {
  attachSpreadDeviationCycleOverlay,
  buildSpreadDeviationCycleMarkers,
} from '@/lib/spreadDeviationCycleChart';
import { generateExchangeInstIds, normalizeSymbolInput } from '@/lib/exchangeInstId';
import { mergePairCategory } from '@/lib/instrumentCategory';
import { chartConfigFromPositionGroup } from './positionGroupChart';
import { InstructionThresholdAxisOverlay } from '@/components/InstructionThresholdStrip';
import { RealtimeSpreadPositions } from './RealtimeSpreadPositions';
import '../OppTop/OppTop.css';
import './RealtimeSpread.css';

const MAX_POINTS = 3000;
const FLUSH_INTERVAL_MS = 150;
const DEFAULT_CHART_HEIGHT_FULL = 1240;
const DEFAULT_CHART_HEIGHT_SPREAD_PRICE = 920;
const MIN_CHART_VIEWPORT_HEIGHT = 320;
const MAX_CHART_VIEWPORT_HEIGHT = 2800;
const CHART_HEIGHT_STORAGE_KEY_FULL = 'realtime-spread-chart-height-full';
const CHART_HEIGHT_STORAGE_KEY_SPREAD_PRICE = 'realtime-spread-chart-height-spread-price';

function loadChartViewportHeight(storageKey: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const value = Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.min(MAX_CHART_VIEWPORT_HEIGHT, Math.max(MIN_CHART_VIEWPORT_HEIGHT, Math.round(value)));
  } catch {
    return fallback;
  }
}

function saveChartViewportHeight(storageKey: string, value: number): void {
  try {
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* ignore */
  }
}
const PANE_STRETCH_FACTORS = [260, 300, 320, 320, 360] as const;
const SPREAD_PRICE_PANE_STRETCH_FACTORS = [2, 1, 1] as const;
const SPREAD_PRICE_PANE_LABELS = ['Spread', 'K线', 'Funding'] as const;
const FULL_PANE_LABELS = ['Spread', 'K线', 'Bias', 'Index', 'Offset MA'] as const;
const PANE_LAYOUT_STORAGE_KEY_SPREAD = 'realtime-spread-pane-layout-spread-price';
const PANE_LAYOUT_STORAGE_KEY_FULL = 'realtime-spread-pane-layout-full';
const HIDDEN_PANE_HEIGHT = 0;
const OPP_PRICE_DATA_SOURCE_STORAGE_KEY = 'opp-spread-price-data-source';

function loadOppPriceDataSource(): OppSpreadPriceDataSource {
  try {
    const raw = localStorage.getItem(OPP_PRICE_DATA_SOURCE_STORAGE_KEY);
    if (raw === 'trade') return 'trade';
  } catch {
    /* ignore */
  }
  return 'l1';
}

function saveOppPriceDataSource(source: OppSpreadPriceDataSource): void {
  try {
    localStorage.setItem(OPP_PRICE_DATA_SOURCE_STORAGE_KEY, source);
  } catch {
    /* ignore */
  }
}

type KlineDisplayMode = 'candle' | 'close-line';

const KLINE_DISPLAY_MODE_STORAGE_KEY = 'realtime-spread-kline-display-mode';

const KLINE_PRICE_FORMAT = { type: 'price' as const, precision: 4, minMove: 0.0001 };

function loadKlineDisplayMode(): KlineDisplayMode {
  try {
    const raw = localStorage.getItem(KLINE_DISPLAY_MODE_STORAGE_KEY);
    if (raw === 'close-line' || raw === 'candle') return raw;
  } catch {
    /* ignore */
  }
  return 'candle';
}

function saveKlineDisplayMode(mode: KlineDisplayMode): void {
  try {
    localStorage.setItem(KLINE_DISPLAY_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

function klineDisplayModeLabel(mode: KlineDisplayMode): string {
  return mode === 'candle' ? 'Close 连线' : 'K 线蜡烛';
}

function oppPriceDataSourceLabel(source: OppSpreadPriceDataSource): string {
  return source === 'l1' ? 'L1 盘口' : 'Trade 成交';
}

export type ChartPaneLayout = {
  stretch: number[];
  visible: boolean[];
};

const CHART_PANES_LAYOUT = {
  enableResize: true,
  separatorColor: '#cbd5e1',
  separatorHoverColor: 'rgba(124, 58, 237, 0.25)',
} as const;

function defaultChartPaneLayout(spreadPriceMode: boolean): ChartPaneLayout {
  if (spreadPriceMode) {
    return {
      stretch: [...SPREAD_PRICE_PANE_STRETCH_FACTORS],
      visible: [true, true, true],
    };
  }
  return {
    stretch: [...PANE_STRETCH_FACTORS],
    visible: [true, true, true, true, true],
  };
}

function loadChartPaneLayout(storageKey: string, spreadPriceMode: boolean): ChartPaneLayout {
  const fallback = defaultChartPaneLayout(spreadPriceMode);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ChartPaneLayout> & { collapsed?: boolean[] };
    const stretch = Array.isArray(parsed.stretch)
      ? parsed.stretch.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
      : fallback.stretch;
    let visible: boolean[];
    if (Array.isArray(parsed.visible)) {
      visible = parsed.visible.map(Boolean);
    } else if (Array.isArray(parsed.collapsed)) {
      visible = parsed.collapsed.map((collapsed) => !collapsed);
    } else {
      visible = fallback.visible;
    }
    if (stretch.length !== fallback.stretch.length) return fallback;
    return {
      stretch,
      visible: visible.length === fallback.visible.length ? visible : fallback.visible,
    };
  } catch {
    return fallback;
  }
}

function saveChartPaneLayout(storageKey: string, layout: ChartPaneLayout): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch {
    /* ignore */
  }
}

function applyChartPaneLayout(chart: IChartApi, layout: ChartPaneLayout): void {
  chart.panes().forEach((pane, index) => {
    const visible = layout.visible[index] ?? true;
    const paneEl = pane.getHTMLElement();
    if (!visible) {
      pane.setHeight(HIDDEN_PANE_HEIGHT);
      pane.getSeries().forEach((series) => series.applyOptions({ visible: false }));
      if (paneEl) {
        paneEl.style.display = 'none';
      }
      return;
    }
    if (paneEl) {
      paneEl.style.display = '';
    }
    const stretch = layout.stretch[index];
    if (stretch != null && stretch > 0) {
      pane.setStretchFactor(stretch);
    }
  });
}

function snapshotChartPaneStretch(chart: IChartApi, layout: ChartPaneLayout): ChartPaneLayout {
  const stretch = chart.panes().map((pane, index) => {
    if (!(layout.visible[index] ?? true)) {
      return layout.stretch[index] ?? pane.getStretchFactor();
    }
    return pane.getStretchFactor();
  });
  return { ...layout, stretch };
}
const DEFAULT_SMA_PERIOD = 1000;

type StreamStatus = 'idle' | 'history_loading' | 'connecting' | 'live' | 'reconnecting' | 'stale' | 'error';

type SpreadPoint = LineData<UTCTimestamp>;
type CandlePoint = CandlestickData<UTCTimestamp>;

const mapCandlesToCloseLine = (candles: CandlePoint[]): LineData<UTCTimestamp>[] =>
  candles.map(({ time, close }) => ({ time, value: close }));

interface RealtimeSpreadConfig {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  interval: number;
  direction: RealtimeSpreadDirection;
  smaPeriod: number;
  exchange1Fee: number;
  exchange2Fee: number;
  minProfitPercentage: number;
}

function positionGroupUsdValue(group: PositionGroupView): number {
  const longValue = group.long_position?.usd_value;
  const shortValue = group.short_position?.usd_value;
  const values = [longValue, shortValue]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  return values.reduce((sum, value) => sum + Math.abs(value), 0);
}

function pickLargestPositionGroup(groups: PositionGroupView[]): PositionGroupView | null {
  let best: PositionGroupView | null = null;
  let bestValue = 0;

  for (const group of groups) {
    const value = positionGroupUsdValue(group);
    if (value > bestValue) {
      best = group;
      bestValue = value;
    }
  }

  return bestValue > 0 ? best : null;
}

interface PendingUpdates {
  long1Short2OpenSpread: SpreadPoint[];
  long1Short2CloseSpread: SpreadPoint[];
  short1Long2OpenSpread: SpreadPoint[];
  short1Long2CloseSpread: SpreadPoint[];
  biasExchange1: SpreadPoint[];
  biasExchange2: SpreadPoint[];
  long1Short2IndexSpread: SpreadPoint[];
  short1Long2IndexSpread: SpreadPoint[];
  long1Short2AdjustedSpreadMa1m: SpreadPoint[];
  long1Short2AdjustedSpreadMa5m: SpreadPoint[];
  long1Short2AdjustedSpreadMa10m: SpreadPoint[];
  long1Short2AdjustedSpreadMa60m: SpreadPoint[];
  short1Long2AdjustedSpreadMa1m: SpreadPoint[];
  short1Long2AdjustedSpreadMa5m: SpreadPoint[];
  short1Long2AdjustedSpreadMa10m: SpreadPoint[];
  short1Long2AdjustedSpreadMa60m: SpreadPoint[];
  exchange1Kline: CandlePoint[];
  exchange2Kline: CandlePoint[];
}

interface SpreadSeriesData {
  long1Short2Open: SpreadPoint[];
  long1Short2Close: SpreadPoint[];
  short1Long2Open: SpreadPoint[];
  short1Long2Close: SpreadPoint[];
  long1Short2OpenLine: SpreadPoint[];
  long1Short2CloseLine: SpreadPoint[];
  short1Long2OpenLine: SpreadPoint[];
  short1Long2CloseLine: SpreadPoint[];
  long1Short2MidSpread: SpreadPoint[];
  short1Long2MidSpread: SpreadPoint[];
  long1Short2MedianMidSpread: SpreadPoint[];
  short1Long2MedianMidSpread: SpreadPoint[];
}

interface MetricSeriesData {
  long1Short2IndexSpread: SpreadPoint[];
  short1Long2IndexSpread: SpreadPoint[];
  long1Short2AdjustedSpreadMa1m: SpreadPoint[];
  long1Short2AdjustedSpreadMa5m: SpreadPoint[];
  long1Short2AdjustedSpreadMa10m: SpreadPoint[];
  long1Short2AdjustedSpreadMa60m: SpreadPoint[];
  short1Long2AdjustedSpreadMa1m: SpreadPoint[];
  short1Long2AdjustedSpreadMa5m: SpreadPoint[];
  short1Long2AdjustedSpreadMa10m: SpreadPoint[];
  short1Long2AdjustedSpreadMa60m: SpreadPoint[];
}

interface OffsetMaSeriesData {
  ma1m: SpreadPoint[];
  ma5m: SpreadPoint[];
  ma10m: SpreadPoint[];
  ma60m: SpreadPoint[];
}

interface ChartSeriesVisibility {
  openSpread: boolean;
  closeSpread: boolean;
  openLine: boolean;
  closeLine: boolean;
  midSpread: boolean;
  medianMidSpread: boolean;
  fundingExchange1: boolean;
  fundingExchange2: boolean;
  netFunding: boolean;
  klineExchange1: boolean;
  klineExchange2: boolean;
  deviationCycles: boolean;
  biasExchange1: boolean;
  biasExchange2: boolean;
  indexSpread: boolean;
  offsetMa1m: boolean;
  offsetMa5m: boolean;
  offsetMa10m: boolean;
  offsetMa60m: boolean;
}

interface MetricHistoryData {
  mappedBiasExchange1: SpreadPoint[];
  mappedBiasExchange2: SpreadPoint[];
  mappedMetricSeries: MetricSeriesData;
}

interface TooltipRow {
  label: string;
  value: string;
  color: string;
}

const toDateTimeInput = (date: Date): string => date.toISOString().slice(0, 16);

const inputUtcToIso = (value: string): string => {
  if (!value) return '';
  return new Date(`${value}:00Z`).toISOString();
};

const INTERVAL_PRESETS = [
  { label: '30s', shortLabel: '30s', seconds: 30, defaultMs: 24 * 60 * 60 * 1000, maxMs: 48 * 60 * 60 * 1000 },
  { label: '1min', shortLabel: '1m', seconds: 60, defaultMs: 2 * 24 * 60 * 60 * 1000, maxMs: 6 * 24 * 60 * 60 * 1000 },
  { label: '5min', shortLabel: '5m', seconds: 300, defaultMs: 6 * 24 * 60 * 60 * 1000, maxMs: 12 * 24 * 60 * 60 * 1000 },
  { label: '10min', shortLabel: '10m', seconds: 600, defaultMs: 9 * 24 * 60 * 60 * 1000, maxMs: 18 * 24 * 60 * 60 * 1000 },
  { label: '30min', shortLabel: '30m', seconds: 1800, defaultMs: 12 * 24 * 60 * 60 * 1000, maxMs: 24 * 24 * 60 * 60 * 1000 },
  { label: '1h', shortLabel: '1h', seconds: 3600, defaultMs: 24 * 24 * 60 * 60 * 1000, maxMs: 48 * 24 * 60 * 60 * 1000 },
  { label: '4h', shortLabel: '4h', seconds: 14400, defaultMs: 48 * 24 * 60 * 60 * 1000, maxMs: 96 * 24 * 60 * 60 * 1000 },
  { label: '8h', shortLabel: '8h', seconds: 28800, defaultMs: 96 * 24 * 60 * 60 * 1000, maxMs: 192 * 24 * 60 * 60 * 1000 },
  { label: '24h', shortLabel: '1d', seconds: 86400, defaultMs: 192 * 24 * 60 * 60 * 1000, maxMs: 384 * 24 * 60 * 60 * 1000 },
] as const;

const DEFAULT_INTERVAL_SECONDS = 60;

function getIntervalPreset(seconds: number) {
  return INTERVAL_PRESETS.find((item) => item.seconds === seconds)
    ?? INTERVAL_PRESETS.find((item) => item.seconds === DEFAULT_INTERVAL_SECONDS)!;
}

function defaultStartForInterval(seconds: number): string {
  const preset = getIntervalPreset(seconds);
  return toDateTimeInput(new Date(Date.now() - preset.defaultMs));
}

function startTimeBoundsForInterval(seconds: number): { min: string; max: string } {
  const preset = getIntervalPreset(seconds);
  const now = Date.now();
  return {
    min: toDateTimeInput(new Date(now - preset.maxMs)),
    max: toDateTimeInput(new Date(now)),
  };
}

function clampStartTimeInput(value: string, seconds: number): string {
  if (!value) return defaultStartForInterval(seconds);
  const bounds = startTimeBoundsForInterval(seconds);
  const ms = new Date(inputUtcToIso(value)).getTime();
  const minMs = new Date(inputUtcToIso(bounds.min)).getTime();
  const maxMs = new Date(inputUtcToIso(bounds.max)).getTime();
  if (!Number.isFinite(ms)) return defaultStartForInterval(seconds);
  if (ms < minMs) return bounds.min;
  if (ms > maxMs) return bounds.max;
  return value;
}

function formatLookbackDuration(ms: number): string {
  const hours = ms / (60 * 60 * 1000);
  if (hours < 48 && hours % 24 !== 0) {
    return `${Math.round(hours)} 小时`;
  }
  const days = Math.round(hours / 24);
  return `${days} 天`;
}

const toUiExchange = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return value;
};

function hasChartSearchParams(params: URLSearchParams): boolean {
  return Boolean(
    params.get('symbol')?.trim() ||
      params.get('exchange1')?.trim() ||
      params.get('exchange2')?.trim() ||
      params.get('instId1')?.trim() ||
      params.get('instId2')?.trim(),
  );
}

const toIndexExchange = (value: string): IndexPriceExchange | null => {
  const normalized = value.trim().toLowerCase();
  if (['bybit', 'okx', 'binance', 'gateio', 'bitget'].includes(normalized)) {
    return normalized as IndexPriceExchange;
  }
  return null;
};

const toIndexInterval = (seconds: number): string => {
  const safeSeconds = Math.max(1, Math.floor(seconds));
  if (safeSeconds % 86400 === 0) return `${safeSeconds / 86400}d`;
  if (safeSeconds % 3600 === 0) return `${safeSeconds / 3600}h`;
  if (safeSeconds % 60 === 0) return `${safeSeconds / 60}m`;
  return `${safeSeconds}s`;
};

const toUtcTimestamp = (timestamp: string): UTCTimestamp | null => {
  const ms = new Date(timestamp).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000) as UTCTimestamp;
};

const timeToUtcDate = (time: Time): Date => {
  if (typeof time === 'number') return new Date(time * 1000);
  if (typeof time === 'string') return new Date(`${time}T00:00:00Z`);
  return new Date(Date.UTC(time.year, time.month - 1, time.day));
};

const formatUtcDateTime = (time: Time): string => {
  const date = timeToUtcDate(time);
  const pad2 = (value: number): string => String(value).padStart(2, '0');
  return [
    date.getUTCFullYear(),
    '-',
    pad2(date.getUTCMonth() + 1),
    '-',
    pad2(date.getUTCDate()),
    ' ',
    pad2(date.getUTCHours()),
    ':',
    pad2(date.getUTCMinutes()),
    ':',
    pad2(date.getUTCSeconds()),
  ].join('');
};

const formatTooltipValue = (value: number): string => {
  if (!Number.isFinite(value)) return '-';
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toPrecision(6);
};

/** Normalize legacy API values that returned instrument minutes in funding_interval_hours. */
const normalizeFundingIntervalHours = (raw: number | null | undefined): number | null => {
  if (raw == null || !Number.isFinite(raw) || raw <= 0) return null;
  if (raw > 24) return raw / 60;
  return raw;
};

const buildFundingSummaryFromResponses = (
  funding1: OppFundingHistoryResponse | null,
  funding2: OppFundingHistoryResponse | null,
) => ({
  exchange1CurrentRate: funding1?.current_rate ?? null,
  exchange2CurrentRate: funding2?.current_rate ?? null,
  exchange1NextFundingTimeMs: funding1?.next_funding_time_ms ?? null,
  exchange2NextFundingTimeMs: funding2?.next_funding_time_ms ?? null,
});

const spreadDirectionShortLabel = (direction: RealtimeSpreadDirection): string =>
  direction === 'long1_short2' ? 'L1S2' : 'S1L2';

type DirectionLegFunding = {
  longRateImpact: number | null;
  longNextMs: number | null;
  shortRateImpact: number | null;
  shortNextMs: number | null;
};

/** Long pays +rate, short receives +rate → net rate impact per leg. */
function directionLegFunding(
  direction: RealtimeSpreadDirection,
  exchange1Rate: number | null,
  exchange1NextMs: number | null,
  exchange2Rate: number | null,
  exchange2NextMs: number | null,
): DirectionLegFunding {
  const longRate = direction === 'long1_short2' ? exchange1Rate : exchange2Rate;
  const longNextMs = direction === 'long1_short2' ? exchange1NextMs : exchange2NextMs;
  const shortRate = direction === 'long1_short2' ? exchange2Rate : exchange1Rate;
  const shortNextMs = direction === 'long1_short2' ? exchange2NextMs : exchange1NextMs;

  return {
    longRateImpact: longRate != null && Number.isFinite(longRate) ? -longRate : null,
    longNextMs,
    shortRateImpact: shortRate != null && Number.isFinite(shortRate) ? shortRate : null,
    shortNextMs,
  };
}

function computeNextNetFundingRate(legs: DirectionLegFunding): {
  netRate: number | null;
  nextSettlementMs: number | null;
  settlingBoth: boolean;
} {
  const { longRateImpact, longNextMs, shortRateImpact, shortNextMs } = legs;

  if (longNextMs != null && shortNextMs != null) {
    if (longNextMs === shortNextMs) {
      if (longRateImpact == null || shortRateImpact == null) {
        return { netRate: null, nextSettlementMs: longNextMs, settlingBoth: true };
      }
      return {
        netRate: longRateImpact + shortRateImpact,
        nextSettlementMs: longNextMs,
        settlingBoth: true,
      };
    }
    if (longNextMs < shortNextMs) {
      return {
        netRate: longRateImpact,
        nextSettlementMs: longNextMs,
        settlingBoth: false,
      };
    }
    return {
      netRate: shortRateImpact,
      nextSettlementMs: shortNextMs,
      settlingBoth: false,
    };
  }

  if (longNextMs != null) {
    return { netRate: longRateImpact, nextSettlementMs: longNextMs, settlingBoth: false };
  }
  if (shortNextMs != null) {
    return { netRate: shortRateImpact, nextSettlementMs: shortNextMs, settlingBoth: false };
  }
  return { netRate: null, nextSettlementMs: null, settlingBoth: false };
}

const getSeriesData = <T,>(seriesData: Map<unknown, unknown>, series: unknown): T | undefined =>
  seriesData.get(series) as T | undefined;

const emptySpreadSeriesData = (): SpreadSeriesData => ({
  long1Short2Open: [],
  long1Short2Close: [],
  short1Long2Open: [],
  short1Long2Close: [],
  long1Short2OpenLine: [],
  long1Short2CloseLine: [],
  short1Long2OpenLine: [],
  short1Long2CloseLine: [],
  long1Short2MidSpread: [],
  short1Long2MidSpread: [],
  long1Short2MedianMidSpread: [],
  short1Long2MedianMidSpread: [],
});

const emptyMetricSeriesData = (): MetricSeriesData => ({
  long1Short2IndexSpread: [],
  short1Long2IndexSpread: [],
  long1Short2AdjustedSpreadMa1m: [],
  long1Short2AdjustedSpreadMa5m: [],
  long1Short2AdjustedSpreadMa10m: [],
  long1Short2AdjustedSpreadMa60m: [],
  short1Long2AdjustedSpreadMa1m: [],
  short1Long2AdjustedSpreadMa5m: [],
  short1Long2AdjustedSpreadMa10m: [],
  short1Long2AdjustedSpreadMa60m: [],
});

const emptyPendingUpdates = (): PendingUpdates => ({
  long1Short2OpenSpread: [],
  long1Short2CloseSpread: [],
  short1Long2OpenSpread: [],
  short1Long2CloseSpread: [],
  biasExchange1: [],
  biasExchange2: [],
  long1Short2IndexSpread: [],
  short1Long2IndexSpread: [],
  long1Short2AdjustedSpreadMa1m: [],
  long1Short2AdjustedSpreadMa5m: [],
  long1Short2AdjustedSpreadMa10m: [],
  long1Short2AdjustedSpreadMa60m: [],
  short1Long2AdjustedSpreadMa1m: [],
  short1Long2AdjustedSpreadMa5m: [],
  short1Long2AdjustedSpreadMa10m: [],
  short1Long2AdjustedSpreadMa60m: [],
  exchange1Kline: [],
  exchange2Kline: [],
});

const defaultChartSeriesVisibility = (): ChartSeriesVisibility => ({
  openSpread: true,
  closeSpread: true,
  openLine: false,
  closeLine: false,
  midSpread: true,
  medianMidSpread: true,
  fundingExchange1: true,
  fundingExchange2: true,
  netFunding: true,
  klineExchange1: true,
  klineExchange2: true,
  deviationCycles: false,
  biasExchange1: true,
  biasExchange2: true,
  indexSpread: true,
  offsetMa1m: true,
  offsetMa5m: true,
  offsetMa10m: true,
  offsetMa60m: true,
});

const createTooltip = (container: HTMLDivElement): HTMLDivElement => {
  const tooltip = document.createElement('div');
  tooltip.className = 'realtime-spread-tooltip';
  tooltip.style.display = 'none';
  container.appendChild(tooltip);
  return tooltip;
};

const renderTooltip = (
  tooltip: HTMLDivElement,
  title: string,
  rows: TooltipRow[],
) => {
  tooltip.replaceChildren();

  const titleEl = document.createElement('div');
  titleEl.className = 'realtime-spread-tooltip-title';
  titleEl.textContent = title;
  tooltip.appendChild(titleEl);

  rows.forEach((row) => {
    const rowEl = document.createElement('div');
    rowEl.className = 'realtime-spread-tooltip-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'realtime-spread-tooltip-label';
    const dot = document.createElement('i');
    dot.className = 'realtime-spread-tooltip-dot';
    dot.style.background = row.color;
    labelEl.append(dot, document.createTextNode(row.label));

    const valueEl = document.createElement('span');
    valueEl.className = 'realtime-spread-tooltip-value';
    valueEl.textContent = row.value;

    rowEl.append(labelEl, valueEl);
    tooltip.appendChild(rowEl);
  });
};

const positionTooltip = (tooltip: HTMLDivElement, container: HTMLDivElement, point: { x: number; y: number }) => {
  const padding = 12;
  const offset = 16;
  tooltip.style.display = 'block';

  const tooltipWidth = tooltip.offsetWidth;
  const tooltipHeight = tooltip.offsetHeight;
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;

  let left = point.x + offset;
  let top = point.y + offset;

  if (left + tooltipWidth + padding > containerWidth) {
    left = point.x - tooltipWidth - offset;
  }
  if (top + tooltipHeight + padding > containerHeight) {
    top = point.y - tooltipHeight - offset;
  }

  left = Math.max(padding, Math.min(left, containerWidth - tooltipWidth - padding));
  top = Math.max(padding, Math.min(top, containerHeight - tooltipHeight - padding));

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

/** lightweight-charts 多 pane 时 param.point.y 相对当前 pane，需用容器内真实鼠标坐标。 */
const createChartPointerTracker = (container: HTMLDivElement) => {
  let pointer: { x: number; y: number } | null = null;

  const syncPointer = (event: PointerEvent) => {
    const rect = container.getBoundingClientRect();
    pointer = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const clearPointer = () => {
    pointer = null;
  };

  container.addEventListener('pointermove', syncPointer);
  container.addEventListener('pointerleave', clearPointer);

  return {
    getPointer: () => pointer,
    dispose: () => {
      container.removeEventListener('pointermove', syncPointer);
      container.removeEventListener('pointerleave', clearPointer);
    },
  };
};

const toIntervalBucketTs = (ts: number, intervalSeconds: number): UTCTimestamp => {
  const safeInterval = Math.max(1, Math.floor(intervalSeconds));
  return (Math.floor(ts / safeInterval) * safeInterval) as UTCTimestamp;
};

const sortByTime = <T extends { time: UTCTimestamp }>(data: T[]): T[] =>
  [...data].sort((a, b) => Number(a.time) - Number(b.time));

const rollingAverage = <T extends { time: UTCTimestamp }>(
  rows: T[],
  index: number,
  windowSeconds: number,
  getValue: (row: T) => number,
): number => {
  const minTime = Number(rows[index].time) - windowSeconds;
  let sum = 0;
  let count = 0;
  for (let i = index; i >= 0; i -= 1) {
    if (Number(rows[i].time) < minTime) break;
    sum += getValue(rows[i]);
    count += 1;
  }
  return count > 0 ? sum / count : getValue(rows[index]);
};

const upsertLine = (base: SpreadPoint[], point: SpreadPoint): SpreadPoint[] => {
  if (base.length === 0) return [point];
  const next = [...base];
  const last = next[next.length - 1];
  if (point.time === last.time) {
    next[next.length - 1] = point;
  } else if (Number(point.time) > Number(last.time)) {
    next.push(point);
  } else {
    const index = next.findIndex((item) => item.time === point.time);
    if (index >= 0) {
      next[index] = point;
    } else {
      const insertAt = next.findIndex((item) => Number(item.time) > Number(point.time));
      if (insertAt < 0) {
        next.push(point);
      } else {
        next.splice(insertAt, 0, point);
      }
    }
  }
  if (next.length > MAX_POINTS) return next.slice(next.length - MAX_POINTS);
  return next;
};

const upsertCandle = (base: CandlePoint[], point: CandlePoint): CandlePoint[] => {
  if (base.length === 0) return [point];
  const next = [...base];
  const last = next[next.length - 1];
  if (point.time === last.time) {
    next[next.length - 1] = point;
  } else if (Number(point.time) > Number(last.time)) {
    next.push(point);
  } else {
    const index = next.findIndex((item) => item.time === point.time);
    if (index >= 0) {
      next[index] = point;
    } else {
      const insertAt = next.findIndex((item) => Number(item.time) > Number(point.time));
      if (insertAt < 0) {
        next.push(point);
      } else {
        next.splice(insertAt, 0, point);
      }
    }
  }
  if (next.length > MAX_POINTS) return next.slice(next.length - MAX_POINTS);
  return next;
};

const indexCandlesToMap = (data: IndexPriceCandle[], intervalSeconds: number): Map<number, number> => {
  const map = new Map<number, number>();
  data.forEach((point) => {
    const time = toIntervalBucketTs(Math.floor(point.ts_ms / 1000), intervalSeconds);
    if (Number.isFinite(point.close) && point.close > 0) {
      map.set(Number(time), point.close);
    }
  });
  return map;
};

const mapDetailedSpreadLine = (
  detailedData: DetailedSpreadDataPoint[],
  field: keyof DetailedSpreadDataPoint,
  intervalSeconds: number,
  scaleToPercent = true,
): SpreadPoint[] =>
  sortByTime(
    detailedData
      .map((point): SpreadPoint | null => {
        const rawTime = toUtcTimestamp(point.timestamp);
        const value = point[field];
        if (rawTime == null || typeof value !== 'number' || !Number.isFinite(value)) return null;
        const time = toIntervalBucketTs(Number(rawTime), intervalSeconds);
        return { time, value: scaleToPercent ? value * 100 : value };
      })
      .filter((point): point is SpreadPoint => point != null),
  );

const mapTradePriceSpreadSeries = (
  points: OppTradePriceSpreadPoint[],
  intervalSeconds: number,
): SpreadSeriesData => {
  const long1Short2MidSpread = sortByTime(
    points
      .map((point): SpreadPoint | null => {
        if (!Number.isFinite(point.spread) || !Number.isFinite(point.tms)) return null;
        const time = toIntervalBucketTs(Math.floor(point.tms / 1000), intervalSeconds);
        return { time, value: point.spread };
      })
      .filter((point): point is SpreadPoint => point != null),
  );
  return {
    ...emptySpreadSeriesData(),
    long1Short2MidSpread,
    short1Long2MidSpread: long1Short2MidSpread.map((point) => ({
      time: point.time,
      value: -point.value,
    })),
  };
};

const mapApiCandles = (data: CandleDataPoint[], intervalSeconds: number): CandlePoint[] =>
  sortByTime(
    data
      .map((point): CandlePoint | null => {
        const rawTime = toUtcTimestamp(point.timestamp);
        if (rawTime == null) return null;
        const time = toIntervalBucketTs(Number(rawTime), intervalSeconds);
        return { time, open: point.open, high: point.high, low: point.low, close: point.close };
      })
      .filter((point): point is CandlePoint => point != null),
  );

const pickOppFundingSamples = (
  payload: OppFundingHistoryResponse,
): Array<OppFundingRateSample | OppResampledFundingPoint> => {
  if (payload.resampled && payload.resampled.length > 0) return payload.resampled;
  return payload.samples;
};

const mapOppFundingLine = (
  payload: OppFundingHistoryResponse | null,
  intervalSeconds: number,
): SpreadPoint[] => {
  if (!payload) return [];
  return sortByTime(
    pickOppFundingSamples(payload)
      .map((point): SpreadPoint | null => {
        if (!Number.isFinite(point.rate) || !Number.isFinite(point.ts_ms) || point.ts_ms <= 0) return null;
        const time = toIntervalBucketTs(Math.floor(point.ts_ms / 1000), intervalSeconds);
        return { time, value: point.rate * 100 };
      })
      .filter((point): point is SpreadPoint => point != null),
  );
};

const fundingTimeToleranceMs = (intervalSeconds: number): number =>
  Math.max(5_000, Math.floor(intervalSeconds) * 1_000);

const fundingTimesDiffer = (left: number, right: number, intervalSeconds: number): boolean =>
  Math.abs(left - right) > fundingTimeToleranceMs(intervalSeconds);

const findNearestFundingLinePoint = (
  line: SpreadPoint[],
  targetTimeSec: number,
  intervalSeconds: number,
): SpreadPoint | null => {
  if (line.length === 0) return null;

  const tolerance = Math.max(1, Math.floor(intervalSeconds));
  let nearest: SpreadPoint | null = null;
  let nearestDist = Infinity;

  for (const point of line) {
    const dist = Math.abs(Number(point.time) - targetTimeSec);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = point;
    }
  }

  if (nearest && nearestDist <= tolerance) return nearest;
  return null;
};

const pickOppFundingSettlementSamples = (
  payload: OppFundingHistoryResponse,
): Array<OppFundingRateSample | OppResampledFundingPoint> => {
  if (payload.samples.length > 0) return payload.samples;
  return pickOppFundingSamples(payload);
};

const mapOppFundingSettlements = (
  payload: OppFundingHistoryResponse | null,
  intervalSeconds: number,
  lineData: SpreadPoint[],
): SpreadPoint[] => {
  if (!payload || lineData.length === 0) return [];

  const sorted = [...pickOppFundingSettlementSamples(payload)].sort((a, b) => a.ts_ms - b.ts_ms);
  if (sorted.length === 0) return [];
  const settlementTimeMs = new Set<number>();
  const tsToleranceMs = fundingTimeToleranceMs(intervalSeconds);

  for (let index = 0; index < sorted.length; index += 1) {
    const sample = sorted[index];
    const fundingTimeMs = sample.funding_time_ms;
    if (fundingTimeMs == null || fundingTimeMs <= 0) continue;

    const nextFundingTimeMs = sorted[index + 1]?.funding_time_ms;
    if (nextFundingTimeMs != null && fundingTimesDiffer(nextFundingTimeMs, fundingTimeMs, intervalSeconds)) {
      settlementTimeMs.add(fundingTimeMs);
      continue;
    }

    if (sample.ts_ms + tsToleranceMs >= fundingTimeMs) {
      settlementTimeMs.add(fundingTimeMs);
    }
  }

  const byTime = new Map<number, SpreadPoint>();
  for (const fundingTimeMs of settlementTimeMs) {
    const fundingTimeSec = Math.floor(fundingTimeMs / 1000);
    const linePoint = findNearestFundingLinePoint(lineData, fundingTimeSec, intervalSeconds);
    if (!linePoint || !Number.isFinite(linePoint.value)) continue;
    byTime.set(Number(linePoint.time), { time: linePoint.time, value: linePoint.value });
  }
  return sortByTime([...byTime.values()]);
};

const alignSettlementsToFundingLine = (
  settlements: SpreadPoint[],
  line: SpreadPoint[],
  intervalSeconds: number,
): SpreadPoint[] => {
  const aligned: SpreadPoint[] = [];
  const seen = new Set<number>();
  for (const settlement of settlements) {
    const point =
      line.find((row) => Number(row.time) === Number(settlement.time)) ??
      findNearestFundingLinePoint(line, Number(settlement.time), intervalSeconds);
    if (!point || !Number.isFinite(point.value)) continue;
    const time = Number(point.time);
    if (seen.has(time)) continue;
    seen.add(time);
    aligned.push({ time: point.time, value: point.value });
  }
  return sortByTime(aligned);
};

const toFundingSettlementMarkers = (
  settlements: SpreadPoint[],
  color: string,
): SeriesMarker<Time>[] =>
  settlements.map((point) => ({
    time: point.time,
    position: 'atPriceMiddle',
    shape: 'circle',
    color,
    price: point.value,
    size: 1.4,
  }));

const maxSeriesEndTs = (seriesList: Array<Array<{ time: UTCTimestamp }>>): number =>
  seriesList.reduce((max, series) => {
    const last = series[series.length - 1]?.time;
    return last != null ? Math.max(max, Number(last)) : max;
  }, 0);

const buildMetricHistory = (
  detailedData: DetailedSpreadDataPoint[],
  index1ByTime: Map<number, number>,
  index2ByTime: Map<number, number>,
  intervalSeconds: number,
): MetricHistoryData => {
  const mappedBiasExchange1: SpreadPoint[] = [];
  const mappedBiasExchange2: SpreadPoint[] = [];
  const long1Short2IndexSpread: SpreadPoint[] = [];
  const short1Long2IndexSpread: SpreadPoint[] = [];
  const rows: Array<{
    time: UTCTimestamp;
    long1Short2OrderbookSpread: number;
    short1Long2OrderbookSpread: number;
    long1Short2IndexSpread: number;
    short1Long2IndexSpread: number;
  }> = [];

  detailedData.forEach((point) => {
    const rawTime = toUtcTimestamp(point.timestamp);
    if (rawTime == null) return;
    const time = toIntervalBucketTs(Number(rawTime), intervalSeconds);
    const index1 = index1ByTime.get(Number(time));
    const index2 = index2ByTime.get(Number(time));
    const long1Short2OpenSpread = point.long1_short2_open_spread;
    const short1Long2OpenSpread = point.short1_long2_open_spread;
    if (
      index1 == null ||
      index2 == null ||
      !Number.isFinite(index1) ||
      !Number.isFinite(index2) ||
      index1 <= 0 ||
      index2 <= 0 ||
      !Number.isFinite(long1Short2OpenSpread) ||
      !Number.isFinite(short1Long2OpenSpread)
    ) {
      return;
    }
    const mid1 = (point.bid_price1 + point.ask_price1) / 2;
    const mid2 = (point.bid_price2 + point.ask_price2) / 2;
    const long1Short2Index = (index2 - index1) / index1;
    const short1Long2Index = (index1 - index2) / index2;

    mappedBiasExchange1.push({ time, value: ((mid1 - index1) / index1) * 100 });
    mappedBiasExchange2.push({ time, value: ((mid2 - index2) / index2) * 100 });
    long1Short2IndexSpread.push({ time, value: long1Short2Index * 100 });
    short1Long2IndexSpread.push({ time, value: short1Long2Index * 100 });
    rows.push({
      time,
      long1Short2OrderbookSpread: long1Short2OpenSpread,
      short1Long2OrderbookSpread: short1Long2OpenSpread,
      long1Short2IndexSpread: long1Short2Index,
      short1Long2IndexSpread: short1Long2Index,
    });
  });

  const sortedRows = sortByTime(rows);
  const long1Short2AdjustedSpreadMa1m: SpreadPoint[] = [];
  const long1Short2AdjustedSpreadMa5m: SpreadPoint[] = [];
  const long1Short2AdjustedSpreadMa10m: SpreadPoint[] = [];
  const long1Short2AdjustedSpreadMa60m: SpreadPoint[] = [];
  const short1Long2AdjustedSpreadMa1m: SpreadPoint[] = [];
  const short1Long2AdjustedSpreadMa5m: SpreadPoint[] = [];
  const short1Long2AdjustedSpreadMa10m: SpreadPoint[] = [];
  const short1Long2AdjustedSpreadMa60m: SpreadPoint[] = [];

  sortedRows.forEach((row, index) => {
    const long1Short2Ma1m = rollingAverage(sortedRows, index, 60, (item) => item.long1Short2IndexSpread);
    const long1Short2Ma5m = rollingAverage(sortedRows, index, 300, (item) => item.long1Short2IndexSpread);
    const long1Short2Ma10m = rollingAverage(sortedRows, index, 600, (item) => item.long1Short2IndexSpread);
    const long1Short2Ma60m = rollingAverage(sortedRows, index, 3600, (item) => item.long1Short2IndexSpread);
    const short1Long2Ma1m = rollingAverage(sortedRows, index, 60, (item) => item.short1Long2IndexSpread);
    const short1Long2Ma5m = rollingAverage(sortedRows, index, 300, (item) => item.short1Long2IndexSpread);
    const short1Long2Ma10m = rollingAverage(sortedRows, index, 600, (item) => item.short1Long2IndexSpread);
    const short1Long2Ma60m = rollingAverage(sortedRows, index, 3600, (item) => item.short1Long2IndexSpread);

    long1Short2AdjustedSpreadMa1m.push({ time: row.time, value: (row.long1Short2OrderbookSpread - long1Short2Ma1m) * 100 });
    long1Short2AdjustedSpreadMa5m.push({ time: row.time, value: (row.long1Short2OrderbookSpread - long1Short2Ma5m) * 100 });
    long1Short2AdjustedSpreadMa10m.push({ time: row.time, value: (row.long1Short2OrderbookSpread - long1Short2Ma10m) * 100 });
    long1Short2AdjustedSpreadMa60m.push({ time: row.time, value: (row.long1Short2OrderbookSpread - long1Short2Ma60m) * 100 });
    short1Long2AdjustedSpreadMa1m.push({ time: row.time, value: (row.short1Long2OrderbookSpread - short1Long2Ma1m) * 100 });
    short1Long2AdjustedSpreadMa5m.push({ time: row.time, value: (row.short1Long2OrderbookSpread - short1Long2Ma5m) * 100 });
    short1Long2AdjustedSpreadMa10m.push({ time: row.time, value: (row.short1Long2OrderbookSpread - short1Long2Ma10m) * 100 });
    short1Long2AdjustedSpreadMa60m.push({ time: row.time, value: (row.short1Long2OrderbookSpread - short1Long2Ma60m) * 100 });
  });

  return {
    mappedBiasExchange1: sortByTime(mappedBiasExchange1),
    mappedBiasExchange2: sortByTime(mappedBiasExchange2),
    mappedMetricSeries: {
      long1Short2IndexSpread: sortByTime(long1Short2IndexSpread),
      short1Long2IndexSpread: sortByTime(short1Long2IndexSpread),
      long1Short2AdjustedSpreadMa1m,
      long1Short2AdjustedSpreadMa5m,
      long1Short2AdjustedSpreadMa10m,
      long1Short2AdjustedSpreadMa60m,
      short1Long2AdjustedSpreadMa1m,
      short1Long2AdjustedSpreadMa5m,
      short1Long2AdjustedSpreadMa10m,
      short1Long2AdjustedSpreadMa60m,
    },
  };
};

const applyPaneStretchFactors = (chart: IChartApi, layout: ChartPaneLayout) => {
  applyChartPaneLayout(chart, layout);
};

function RealtimeSpreadPaneChart({
  chartHeight,
  paneLayout,
  onPaneLayoutSnapshot,
  openSpreadData,
  closeSpreadData,
  biasExchange1Data,
  biasExchange2Data,
  indexSpreadData,
  adjustedSpreadMaData,
  seriesVisibility,
  exchange1Data,
  exchange2Data,
  instructionThresholdLines,
  positionOpenSpreadLine,
  chartSpreadScale,
}: {
  chartHeight: number;
  paneLayout: ChartPaneLayout;
  onPaneLayoutSnapshot?: (layout: ChartPaneLayout) => void;
  openSpreadData: SpreadPoint[];
  closeSpreadData: SpreadPoint[];
  biasExchange1Data: SpreadPoint[];
  biasExchange2Data: SpreadPoint[];
  indexSpreadData: SpreadPoint[];
  adjustedSpreadMaData: OffsetMaSeriesData;
  seriesVisibility: ChartSeriesVisibility;
  exchange1Data: CandlePoint[];
  exchange2Data: CandlePoint[];
  instructionThresholdLines: InstructionThresholdLine[];
  positionOpenSpreadLine: PositionOpenSpreadChartLine | null;
  chartSpreadScale: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const openSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const closeSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const biasExchange1SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const biasExchange2SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const indexSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adjustedSpreadMa1mSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adjustedSpreadMa5mSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adjustedSpreadMa10mSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adjustedSpreadMa60mSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const exchange1SeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const exchange2SeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const instructionThresholdPriceLinesRef = useRef<Array<{ line: IPriceLine; series: ISeriesApi<'Line'> }>>([]);
  const positionOpenSpreadPriceLineRef = useRef<{ line: IPriceLine; series: ISeriesApi<'Line'> } | null>(null);
  const instructionThresholdLinesRef = useRef(instructionThresholdLines);
  const positionOpenSpreadLineRef = useRef(positionOpenSpreadLine);
  const chartSpreadScaleRef = useRef(chartSpreadScale);
  instructionThresholdLinesRef.current = instructionThresholdLines;
  positionOpenSpreadLineRef.current = positionOpenSpreadLine;
  chartSpreadScaleRef.current = chartSpreadScale;
  const exchange1DataRef = useRef(exchange1Data);
  const exchange2DataRef = useRef(exchange2Data);
  const openSpreadDataRef = useRef(openSpreadData);
  const closeSpreadDataRef = useRef(closeSpreadData);
  exchange1DataRef.current = exchange1Data;
  exchange2DataRef.current = exchange2Data;
  openSpreadDataRef.current = openSpreadData;
  closeSpreadDataRef.current = closeSpreadData;
  const shouldFollowLatestRef = useRef(true);
  const latestLogicalRef = useRef<number | null>(null);
  const didFitInitialRef = useRef(false);
  const chartHeightRef = useRef(chartHeight);
  chartHeightRef.current = chartHeight;
  const paneLayoutRef = useRef(paneLayout);
  paneLayoutRef.current = paneLayout;

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyChartPaneLayout(chart, paneLayout);
  }, [paneLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onPaneLayoutSnapshot) return undefined;
    const syncStretch = () => {
      const chart = chartRef.current;
      if (!chart) return;
      onPaneLayoutSnapshot(snapshotChartPaneStretch(chart, paneLayoutRef.current));
    };
    container.addEventListener('mouseup', syncStretch);
    return () => container.removeEventListener('mouseup', syncStretch);
  }, [onPaneLayoutSnapshot]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      width: container.clientWidth || 800,
      height: chartHeightRef.current,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#4b5563',
        panes: CHART_PANES_LAYOUT,
      },
      rightPriceScale: { borderColor: '#e5e7eb' },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      timeScale: { borderColor: '#e5e7eb', timeVisible: true, secondsVisible: false },
      grid: {
        vertLines: { color: '#eef2f7' },
        horzLines: { color: '#eef2f7' },
      },
    });
    const openSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Open Spread %',
        color: '#2563eb',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      0,
    );
    const closeSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Close Spread %',
        color: '#dc2626',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      0,
    );
    const exchange1Series = chart.addSeries(CandlestickSeries, {
      title: 'Orderbook K 线 1',
      upColor: '#16a34a',
      downColor: '#dc2626',
      borderVisible: false,
      wickUpColor: '#16a34a',
      wickDownColor: '#dc2626',
      priceFormat: KLINE_PRICE_FORMAT,
    }, 1);
    const exchange2Series = chart.addSeries(
      CandlestickSeries,
      {
        title: 'Orderbook K 线 2',
        upColor: '#0ea5e9',
        downColor: '#9333ea',
        borderVisible: false,
        wickUpColor: '#0ea5e9',
        wickDownColor: '#9333ea',
        priceFormat: KLINE_PRICE_FORMAT,
      },
      1,
    );
    const biasExchange1Series = chart.addSeries(
      LineSeries,
      {
        title: 'Bias 1 %',
        color: '#16a34a',
        lineWidth: 1,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      2,
    );
    const biasExchange2Series = chart.addSeries(
      LineSeries,
      {
        title: 'Bias 2 %',
        color: '#0ea5e9',
        lineWidth: 1,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      2,
    );
    const indexSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Index Spread %',
        color: '#f59e0b',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      3,
    );
    const adjustedSpreadMa1mSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Offset MA 1m %',
        color: '#7c3aed',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      4,
    );
    const adjustedSpreadMa5mSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Offset MA 5m %',
        color: '#2563eb',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      4,
    );
    const adjustedSpreadMa10mSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Offset MA 10m %',
        color: '#0891b2',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      4,
    );
    const adjustedSpreadMa60mSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Offset MA 60m %',
        color: '#be123c',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      },
      4,
    );
    applyPaneStretchFactors(chart, paneLayoutRef.current);
    window.requestAnimationFrame(() => applyPaneStretchFactors(chart, paneLayoutRef.current));

    const tooltip = createTooltip(container);
    const pointerTracker = createChartPointerTracker(container);
    const tooltipSeries = [
      { series: openSpreadSeries, label: 'Open Spread %', color: '#2563eb', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: closeSpreadSeries, label: 'Close Spread %', color: '#dc2626', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: exchange1Series, label: 'Orderbook K 线 1', color: '#16a34a', kind: 'candle' as const },
      { series: exchange2Series, label: 'Orderbook K 线 2', color: '#0ea5e9', kind: 'candle' as const },
      { series: biasExchange1Series, label: 'Bias 1 %', color: '#16a34a', kind: 'line' as const },
      { series: biasExchange2Series, label: 'Bias 2 %', color: '#0ea5e9', kind: 'line' as const },
      { series: indexSpreadSeries, label: 'Index Spread %', color: '#f59e0b', kind: 'line' as const },
      { series: adjustedSpreadMa1mSeries, label: 'Offset MA 1m %', color: '#7c3aed', kind: 'line' as const },
      { series: adjustedSpreadMa5mSeries, label: 'Offset MA 5m %', color: '#2563eb', kind: 'line' as const },
      { series: adjustedSpreadMa10mSeries, label: 'Offset MA 10m %', color: '#0891b2', kind: 'line' as const },
      { series: adjustedSpreadMa60mSeries, label: 'Offset MA 60m %', color: '#be123c', kind: 'line' as const },
    ];
    const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: Time; seriesData: Map<unknown, unknown> }) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipSeries.reduce<TooltipRow[]>((acc, item) => {
          if (item.kind === 'candle') {
            const point = getSeriesData<CandlestickData<Time>>(param.seriesData, item.series);
            if (!point || !('open' in point)) return acc;
            acc.push({
              label: item.label,
              value: formatCandleTooltipValue(point),
              color: item.color,
            });
            return acc;
          }
          const point = getSeriesData<LineData<Time>>(param.seriesData, item.series);
          if (!point || !('value' in point)) return acc;
          const formatValue = 'formatValue' in item && typeof item.formatValue === 'function'
            ? item.formatValue
            : formatTooltipValue;
          acc.push({
            label: item.label,
            value: formatValue(point.value),
            color: item.color,
          });
          return acc;
        }, []);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      const anchor = pointerTracker.getPointer() ?? param.point;
      positionTooltip(tooltip, container, anchor);
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);

    const handleRangeChange = (range: { from: number; to: number } | null) => {
      const latestLogical = latestLogicalRef.current;
      if (!range || latestLogical == null) return;
      const distanceToLatest = latestLogical - range.to;
      shouldFollowLatestRef.current = Math.abs(distanceToLatest) <= 1;
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);
    chartRef.current = chart;
    openSpreadSeriesRef.current = openSpreadSeries;
    closeSpreadSeriesRef.current = closeSpreadSeries;
    biasExchange1SeriesRef.current = biasExchange1Series;
    biasExchange2SeriesRef.current = biasExchange2Series;
    indexSpreadSeriesRef.current = indexSpreadSeries;
    adjustedSpreadMa1mSeriesRef.current = adjustedSpreadMa1mSeries;
    adjustedSpreadMa5mSeriesRef.current = adjustedSpreadMa5mSeries;
    adjustedSpreadMa10mSeriesRef.current = adjustedSpreadMa10mSeries;
    adjustedSpreadMa60mSeriesRef.current = adjustedSpreadMa60mSeries;
    exchange1SeriesRef.current = exchange1Series;
    exchange2SeriesRef.current = exchange2Series;

    reapplyInstructionThresholdPriceLines(
      openSpreadSeries,
      closeSpreadSeries,
      instructionThresholdLinesRef.current,
      chartSpreadScaleRef.current,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeries,
      positionOpenSpreadLineRef.current,
      chartSpreadScaleRef.current,
      positionOpenSpreadPriceLineRef,
    );

    const detachRangeMeasure = attachChartRangeMeasure(container, chart, {
      panes: [
        {
          paneIndex: 0,
          measureMode: 'spread',
          spread: {
            openLabel: 'Open Spread %',
            closeLabel: 'Close Spread %',
            formatValue: formatSpreadFundingValue,
            getOpenSpread: () => openSpreadDataRef.current,
            getCloseSpread: () => closeSpreadDataRef.current,
          },
          series: [],
        },
        {
          paneIndex: 1,
          measureMode: 'kline',
          series: [
            {
              kind: 'candle',
              label: 'Orderbook K 线 1',
              color: '#16a34a',
              getData: () => exchange1DataRef.current,
            },
            {
              kind: 'candle',
              label: 'Orderbook K 线 2',
              color: '#0ea5e9',
              getData: () => exchange2DataRef.current,
            },
          ],
        },
      ],
    });

    const resizeObserver = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth || 800, height: chartHeightRef.current });
      applyPaneStretchFactors(chart, paneLayoutRef.current);
    });
    resizeObserver.observe(container);

    return () => {
      detachRangeMeasure();
      pointerTracker.dispose();
      resizeObserver.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
      chart.remove();
      chartRef.current = null;
      openSpreadSeriesRef.current = null;
      closeSpreadSeriesRef.current = null;
      biasExchange1SeriesRef.current = null;
      biasExchange2SeriesRef.current = null;
      indexSpreadSeriesRef.current = null;
      adjustedSpreadMa1mSeriesRef.current = null;
      adjustedSpreadMa5mSeriesRef.current = null;
      adjustedSpreadMa10mSeriesRef.current = null;
      adjustedSpreadMa60mSeriesRef.current = null;
      exchange1SeriesRef.current = null;
      exchange2SeriesRef.current = null;
      clearInstructionThresholdPriceLines(instructionThresholdPriceLinesRef);
      clearPositionOpenSpreadPriceLine(positionOpenSpreadPriceLineRef);
      shouldFollowLatestRef.current = true;
      latestLogicalRef.current = null;
      didFitInitialRef.current = false;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.applyOptions({ height: chartHeight });
  }, [chartHeight]);

  useEffect(() => {
    if (paneLayout.visible[0] ?? true) {
      openSpreadSeriesRef.current?.applyOptions({ visible: seriesVisibility.openSpread });
      closeSpreadSeriesRef.current?.applyOptions({ visible: seriesVisibility.closeSpread });
    }
    if (paneLayout.visible[1] ?? true) {
      exchange1SeriesRef.current?.applyOptions({ visible: seriesVisibility.klineExchange1 });
      exchange2SeriesRef.current?.applyOptions({ visible: seriesVisibility.klineExchange2 });
    }
    if (paneLayout.visible[2] ?? true) {
      biasExchange1SeriesRef.current?.applyOptions({ visible: seriesVisibility.biasExchange1 });
      biasExchange2SeriesRef.current?.applyOptions({ visible: seriesVisibility.biasExchange2 });
    }
    if (paneLayout.visible[3] ?? true) {
      indexSpreadSeriesRef.current?.applyOptions({ visible: seriesVisibility.indexSpread });
    }
    if (paneLayout.visible[4] ?? true) {
      adjustedSpreadMa1mSeriesRef.current?.applyOptions({ visible: seriesVisibility.offsetMa1m });
      adjustedSpreadMa5mSeriesRef.current?.applyOptions({ visible: seriesVisibility.offsetMa5m });
      adjustedSpreadMa10mSeriesRef.current?.applyOptions({ visible: seriesVisibility.offsetMa10m });
      adjustedSpreadMa60mSeriesRef.current?.applyOptions({ visible: seriesVisibility.offsetMa60m });
    }
  }, [paneLayout.visible, seriesVisibility]);

  useEffect(() => {
    openSpreadSeriesRef.current?.setData(openSpreadData);
    closeSpreadSeriesRef.current?.setData(closeSpreadData);
    biasExchange1SeriesRef.current?.setData(biasExchange1Data);
    biasExchange2SeriesRef.current?.setData(biasExchange2Data);
    indexSpreadSeriesRef.current?.setData(indexSpreadData);
    adjustedSpreadMa1mSeriesRef.current?.setData(adjustedSpreadMaData.ma1m);
    adjustedSpreadMa5mSeriesRef.current?.setData(adjustedSpreadMaData.ma5m);
    adjustedSpreadMa10mSeriesRef.current?.setData(adjustedSpreadMaData.ma10m);
    adjustedSpreadMa60mSeriesRef.current?.setData(adjustedSpreadMaData.ma60m);
    exchange1SeriesRef.current?.setData(exchange1Data);
    exchange2SeriesRef.current?.setData(exchange2Data);
    reapplyInstructionThresholdPriceLines(
      openSpreadSeriesRef.current,
      closeSpreadSeriesRef.current,
      instructionThresholdLinesRef.current,
      chartSpreadScaleRef.current,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeriesRef.current,
      positionOpenSpreadLineRef.current,
      chartSpreadScaleRef.current,
      positionOpenSpreadPriceLineRef,
    );
    const maxPoints = Math.max(
      openSpreadData.length,
      closeSpreadData.length,
      biasExchange1Data.length,
      biasExchange2Data.length,
      indexSpreadData.length,
      adjustedSpreadMaData.ma1m.length,
      adjustedSpreadMaData.ma5m.length,
      adjustedSpreadMaData.ma10m.length,
      adjustedSpreadMaData.ma60m.length,
      exchange1Data.length,
      exchange2Data.length,
    );
    latestLogicalRef.current = maxPoints > 0 ? maxPoints - 1 : null;
    if (maxPoints === 0) return;
    if (!didFitInitialRef.current) {
      chartRef.current?.timeScale().fitContent();
      didFitInitialRef.current = true;
      shouldFollowLatestRef.current = true;
      return;
    }
    if (shouldFollowLatestRef.current) {
      chartRef.current?.timeScale().scrollToRealTime();
    }
  }, [
    adjustedSpreadMaData,
    biasExchange1Data,
    biasExchange2Data,
    closeSpreadData,
    exchange1Data,
    exchange2Data,
    indexSpreadData,
    openSpreadData,
  ]);

  useEffect(() => {
    reapplyInstructionThresholdPriceLines(
      openSpreadSeriesRef.current,
      closeSpreadSeriesRef.current,
      instructionThresholdLines,
      chartSpreadScale,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeriesRef.current,
      positionOpenSpreadLine,
      chartSpreadScale,
      positionOpenSpreadPriceLineRef,
    );
  }, [chartSpreadScale, instructionThresholdLines, positionOpenSpreadLine]);

  return (
    <div
      className="realtime-spread-chart"
      ref={containerRef}
      style={{ height: chartHeight, minHeight: chartHeight }}
    />
  );
}

function SpreadPricePaneChart({
  chartHeight,
  paneLayout,
  onPaneLayoutSnapshot,
  spreadViewMode,
  openSpreadData,
  closeSpreadData,
  openLineData,
  closeLineData,
  midSpreadData,
  medianMidSpreadData,
  direction,
  seriesVisibility,
  deviationCycles,
  intervalSeconds,
  showDeviationCycles,
  exchange1Data,
  exchange2Data,
  exchange1FundingData,
  exchange2FundingData,
  exchange1FundingSettlements,
  exchange2FundingSettlements,
  netFundingSettlementData,
  exchange1Label,
  exchange2Label,
  instructionThresholdLines,
  pairInstructions,
  positionOpenSpreadLine,
  chartSpreadScale,
  klineDisplayMode,
  showTradeVolumeStrip,
  exchange1InstId,
  exchange2InstId,
  showFundingStrip,
  fundingStrip,
}: {
  chartHeight: number;
  paneLayout: ChartPaneLayout;
  onPaneLayoutSnapshot?: (layout: ChartPaneLayout) => void;
  showTradeVolumeStrip: boolean;
  exchange1InstId: string;
  exchange2InstId: string;
  showFundingStrip: boolean;
  fundingStrip: Omit<FundingInfoStripProps, 'variant'>;
  spreadViewMode: DetailedSpreadViewMode;
  openSpreadData: SpreadPoint[];
  closeSpreadData: SpreadPoint[];
  openLineData: SpreadPoint[];
  closeLineData: SpreadPoint[];
  midSpreadData: SpreadPoint[];
  medianMidSpreadData: SpreadPoint[];
  direction: RealtimeSpreadDirection;
  seriesVisibility: Pick<
    ChartSeriesVisibility,
    | 'openSpread'
    | 'closeSpread'
    | 'openLine'
    | 'closeLine'
    | 'midSpread'
    | 'medianMidSpread'
    | 'fundingExchange1'
    | 'fundingExchange2'
    | 'netFunding'
    | 'klineExchange1'
    | 'klineExchange2'
  >;
  deviationCycles: OppSpreadDeviationCyclesResponse | null;
  intervalSeconds: number;
  showDeviationCycles: boolean;
  exchange1Data: CandlePoint[];
  exchange2Data: CandlePoint[];
  exchange1FundingData: SpreadPoint[];
  exchange2FundingData: SpreadPoint[];
  exchange1FundingSettlements: SpreadPoint[];
  exchange2FundingSettlements: SpreadPoint[];
  netFundingSettlementData: LineData<Time>[];
  exchange1Label: string;
  exchange2Label: string;
  instructionThresholdLines: InstructionThresholdLine[];
  pairInstructions: InstructionView[];
  positionOpenSpreadLine: PositionOpenSpreadChartLine | null;
  chartSpreadScale: number;
  klineDisplayMode: KlineDisplayMode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [chartRevision, setChartRevision] = useState(0);
  const openSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const closeSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const openLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const closeLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const midSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const medianMidSpreadSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const exchange1SeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const exchange2SeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const exchange1CloseLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const exchange2CloseLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const exchange1FundingSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const exchange2FundingSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const netFundingSettlementSeriesRef = useRef<ISeriesApi<'Custom'> | null>(null);
  const exchange1FundingMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const exchange2FundingMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const spreadCycleMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const spreadCycleMarkerTargetRef = useRef<ISeriesApi<'Line'> | null>(null);
  const instructionThresholdPriceLinesRef = useRef<Array<{ line: IPriceLine; series: ISeriesApi<'Line'> }>>([]);
  const positionOpenSpreadPriceLineRef = useRef<{ line: IPriceLine; series: ISeriesApi<'Line'> } | null>(null);
  const instructionThresholdLinesRef = useRef(instructionThresholdLines);
  const positionOpenSpreadLineRef = useRef(positionOpenSpreadLine);
  const chartSpreadScaleRef = useRef(chartSpreadScale);
  instructionThresholdLinesRef.current = instructionThresholdLines;
  positionOpenSpreadLineRef.current = positionOpenSpreadLine;
  chartSpreadScaleRef.current = chartSpreadScale;
  const cycleOverlayRef = useRef<{ dispose: () => void; render: () => void } | null>(null);
  const deviationCyclesRef = useRef(deviationCycles);
  const intervalSecondsRef = useRef(intervalSeconds);
  const showDeviationCyclesRef = useRef(showDeviationCycles);
  const lastSpreadTimeMsRef = useRef<number | null>(null);
  deviationCyclesRef.current = deviationCycles;
  intervalSecondsRef.current = intervalSeconds;
  showDeviationCyclesRef.current = showDeviationCycles;
  const exchange1DataRef = useRef(exchange1Data);
  const exchange2DataRef = useRef(exchange2Data);
  const openSpreadDataRef = useRef(openSpreadData);
  const closeSpreadDataRef = useRef(closeSpreadData);
  const midSpreadDataRef = useRef(midSpreadData);
  const medianMidSpreadDataRef = useRef(medianMidSpreadData);
  const spreadViewModeRef = useRef(spreadViewMode);
  const seriesVisibilityRef = useRef(seriesVisibility);
  const exchange1FundingDataRef = useRef(exchange1FundingData);
  const exchange2FundingDataRef = useRef(exchange2FundingData);
  const exchange1FundingSettlementsRef = useRef(exchange1FundingSettlements);
  const exchange2FundingSettlementsRef = useRef(exchange2FundingSettlements);
  const directionRef = useRef(direction);
  const exchange1LabelRef = useRef(exchange1Label);
  const exchange2LabelRef = useRef(exchange2Label);
  const klineDisplayModeRef = useRef(klineDisplayMode);
  exchange1DataRef.current = exchange1Data;
  exchange2DataRef.current = exchange2Data;
  openSpreadDataRef.current = openSpreadData;
  closeSpreadDataRef.current = closeSpreadData;
  midSpreadDataRef.current = midSpreadData;
  medianMidSpreadDataRef.current = medianMidSpreadData;
  spreadViewModeRef.current = spreadViewMode;
  seriesVisibilityRef.current = seriesVisibility;
  exchange1FundingDataRef.current = exchange1FundingData;
  exchange2FundingDataRef.current = exchange2FundingData;
  exchange1FundingSettlementsRef.current = exchange1FundingSettlements;
  exchange2FundingSettlementsRef.current = exchange2FundingSettlements;
  directionRef.current = direction;
  exchange1LabelRef.current = exchange1Label;
  exchange2LabelRef.current = exchange2Label;
  klineDisplayModeRef.current = klineDisplayMode;
  const shouldFollowLatestRef = useRef(true);
  const latestLogicalRef = useRef<number | null>(null);
  const didFitInitialRef = useRef(false);
  const chartHeightRef = useRef(chartHeight);
  chartHeightRef.current = chartHeight;
  const paneLayoutRef = useRef(paneLayout);
  paneLayoutRef.current = paneLayout;

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyChartPaneLayout(chart, paneLayout);
  }, [paneLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onPaneLayoutSnapshot) return undefined;
    const syncStretch = () => {
      const chart = chartRef.current;
      if (!chart) return;
      onPaneLayoutSnapshot(snapshotChartPaneStretch(chart, paneLayoutRef.current));
    };
    container.addEventListener('mouseup', syncStretch);
    return () => container.removeEventListener('mouseup', syncStretch);
  }, [onPaneLayoutSnapshot]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      width: container.clientWidth || 800,
      height: chartHeightRef.current,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#4b5563',
        panes: CHART_PANES_LAYOUT,
      },
      rightPriceScale: { borderColor: '#e5e7eb' },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      timeScale: { borderColor: '#e5e7eb', timeVisible: true, secondsVisible: false },
      grid: {
        vertLines: { color: '#eef2f7' },
        horzLines: { color: '#eef2f7' },
      },
    });
    const openSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Open Spread',
        color: '#2563eb',
        lineWidth: 2,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const closeSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: 'Close Spread',
        color: '#dc2626',
        lineWidth: 2,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const openLineSeries = chart.addSeries(
      LineSeries,
      {
        title: `${direction}_open_line`,
        color: '#16a34a',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const closeLineSeries = chart.addSeries(
      LineSeries,
      {
        title: `${direction}_close_line`,
        color: '#f97316',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const midSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: `${direction}_mid_spread`,
        color: '#2563eb',
        lineWidth: 2,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const medianMidSpreadSeries = chart.addSeries(
      LineSeries,
      {
        title: `median_${direction}_mid_spread`,
        color: '#f97316',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      0,
    );
    const exchange1Series = chart.addSeries(
      CandlestickSeries,
      {
        title: `${exchange1Label} K线`,
        upColor: '#16a34a',
        downColor: '#dc2626',
        borderVisible: false,
        wickUpColor: '#16a34a',
        wickDownColor: '#dc2626',
        priceFormat: KLINE_PRICE_FORMAT,
      },
      1,
    );
    const exchange2Series = chart.addSeries(
      CandlestickSeries,
      {
        title: `${exchange2Label} K线`,
        upColor: '#0ea5e9',
        downColor: '#9333ea',
        borderVisible: false,
        wickUpColor: '#0ea5e9',
        wickDownColor: '#9333ea',
        priceFormat: KLINE_PRICE_FORMAT,
      },
      1,
    );
    const exchange1CloseLineSeries = chart.addSeries(
      LineSeries,
      {
        title: `${exchange1Label} Close`,
        color: '#16a34a',
        lineWidth: 2,
        visible: false,
        priceFormat: KLINE_PRICE_FORMAT,
      },
      1,
    );
    const exchange2CloseLineSeries = chart.addSeries(
      LineSeries,
      {
        title: `${exchange2Label} Close`,
        color: '#0ea5e9',
        lineWidth: 2,
        visible: false,
        priceFormat: KLINE_PRICE_FORMAT,
      },
      1,
    );
    const exchange1FundingSeries = chart.addSeries(
      LineSeries,
      {
        title: `${exchange1Label} Funding %`,
        color: '#16a34a',
        lineWidth: 2,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      2,
    );
    const exchange2FundingSeries = chart.addSeries(
      LineSeries,
      {
        title: `${exchange2Label} Funding %`,
        color: '#0ea5e9',
        lineWidth: 2,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      2,
    );
    const netFundingSettlementSeries = chart.addCustomSeries(
      createNetFundingBarSeriesView(),
      {
        title: `净资费 (${spreadDirectionShortLabel(direction)})`,
        color: NET_FUNDING_POSITIVE_COLOR,
        priceFormat: SPREAD_FUNDING_PRICE_FORMAT,
      },
      2,
    );
    const exchange1FundingMarkers = createSeriesMarkers(exchange1FundingSeries, []);
    const exchange2FundingMarkers = createSeriesMarkers(exchange2FundingSeries, []);

    applyPaneStretchFactors(chart, paneLayoutRef.current);

    cycleOverlayRef.current = attachSpreadDeviationCycleOverlay(container, chart, () => {
      if (!showDeviationCyclesRef.current) {
        return {
          cycles: [],
          openCycle: null,
          intervalSeconds: intervalSecondsRef.current,
          lastTimeMs: lastSpreadTimeMsRef.current,
        };
      }
      return {
        cycles: deviationCyclesRef.current?.cycles ?? [],
        openCycle: deviationCyclesRef.current?.open_cycle ?? null,
        intervalSeconds: intervalSecondsRef.current,
        lastTimeMs: lastSpreadTimeMsRef.current,
      };
    });

    const tooltip = createTooltip(container);
    const pointerTracker = createChartPointerTracker(container);
    const tooltipSeries = [
      { series: openSpreadSeries, label: 'Open Spread', color: '#2563eb', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: closeSpreadSeries, label: 'Close Spread', color: '#dc2626', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: openLineSeries, label: `${direction}_open_line`, color: '#16a34a', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: closeLineSeries, label: `${direction}_close_line`, color: '#f97316', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: midSpreadSeries, label: `${direction}_mid_spread`, color: '#2563eb', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: medianMidSpreadSeries, label: `median_${direction}_mid_spread`, color: '#f97316', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: exchange1Series, label: `${exchange1Label} K线`, color: '#16a34a', kind: 'candle' as const },
      { series: exchange2Series, label: `${exchange2Label} K线`, color: '#0ea5e9', kind: 'candle' as const },
      {
        series: exchange1CloseLineSeries,
        label: `${exchange1Label} Close`,
        color: '#16a34a',
        kind: 'kline-close' as const,
        formatValue: formatAdaptivePrice,
      },
      {
        series: exchange2CloseLineSeries,
        label: `${exchange2Label} Close`,
        color: '#0ea5e9',
        kind: 'kline-close' as const,
        formatValue: formatAdaptivePrice,
      },
      { series: exchange1FundingSeries, label: `${exchange1Label} Funding %`, color: '#16a34a', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      { series: exchange2FundingSeries, label: `${exchange2Label} Funding %`, color: '#0ea5e9', kind: 'line' as const, formatValue: formatSpreadFundingValue },
      {
        series: netFundingSettlementSeries,
        label: `净资费 (${spreadDirectionShortLabel(direction)})`,
        color: NET_FUNDING_POSITIVE_COLOR,
        kind: 'histogram' as const,
        formatValue: formatSpreadFundingValue,
      },
    ];
    const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: Time; seriesData: Map<unknown, unknown> }) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipSeries.reduce<TooltipRow[]>((acc, item) => {
        if (item.kind === 'candle') {
          if (klineDisplayModeRef.current !== 'candle') return acc;
          const point = getSeriesData<CandlestickData<Time>>(param.seriesData, item.series);
          if (!point || !('open' in point)) return acc;
          acc.push({
            label: item.label,
            value: formatCandleTooltipValue(point),
            color: item.color,
          });
          return acc;
        }
        if (item.kind === 'kline-close') {
          if (klineDisplayModeRef.current !== 'close-line') return acc;
          const point = getSeriesData<LineData<Time>>(param.seriesData, item.series);
          if (!point || !('value' in point)) return acc;
          acc.push({
            label: item.label,
            value: formatAdaptivePrice(point.value),
            color: item.color,
          });
          return acc;
        }
        if (item.kind === 'histogram') {
          const point = getSeriesData<NetFundingBarData>(param.seriesData, item.series);
          if (!point || !Number.isFinite(point.value)) return acc;
          const formatValue = 'formatValue' in item && typeof item.formatValue === 'function'
            ? item.formatValue
            : formatTooltipValue;
          acc.push({
            label: item.label,
            value: formatValue(point.value),
            color:
              point.color ??
              (point.value > 0
                ? NET_FUNDING_POSITIVE_COLOR
                : point.value < 0
                  ? NET_FUNDING_NEGATIVE_COLOR
                  : NET_FUNDING_ZERO_COLOR),
          });
          return acc;
        }
        const point = getSeriesData<LineData<Time>>(param.seriesData, item.series);
        if (!point || !('value' in point)) return acc;
        const formatValue = 'formatValue' in item && typeof item.formatValue === 'function'
          ? item.formatValue
          : formatTooltipValue;
        acc.push({
          label: item.label,
          value: formatValue(point.value),
          color: item.color,
        });
        return acc;
      }, []);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      const anchor = pointerTracker.getPointer() ?? param.point;
      positionTooltip(tooltip, container, anchor);
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);

    const handleRangeChange = (range: { from: number; to: number } | null) => {
      const latestLogical = latestLogicalRef.current;
      if (!range || latestLogical == null) return;
      shouldFollowLatestRef.current = Math.abs(latestLogical - range.to) <= 1;
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);
    chartRef.current = chart;
    setChartRevision((value) => value + 1);
    openSpreadSeriesRef.current = openSpreadSeries;
    closeSpreadSeriesRef.current = closeSpreadSeries;
    openLineSeriesRef.current = openLineSeries;
    closeLineSeriesRef.current = closeLineSeries;
    midSpreadSeriesRef.current = midSpreadSeries;
    medianMidSpreadSeriesRef.current = medianMidSpreadSeries;
    exchange1SeriesRef.current = exchange1Series;
    exchange2SeriesRef.current = exchange2Series;
    exchange1CloseLineSeriesRef.current = exchange1CloseLineSeries;
    exchange2CloseLineSeriesRef.current = exchange2CloseLineSeries;
    exchange1FundingSeriesRef.current = exchange1FundingSeries;
    exchange2FundingSeriesRef.current = exchange2FundingSeries;
    netFundingSettlementSeriesRef.current = netFundingSettlementSeries;
    exchange1FundingMarkersRef.current = exchange1FundingMarkers;
    exchange2FundingMarkersRef.current = exchange2FundingMarkers;
    spreadCycleMarkersRef.current = null;
    spreadCycleMarkerTargetRef.current = null;

    if (paneLayoutRef.current.visible[0] ?? true) {
      const isMid = spreadViewModeRef.current === 'mid';
      const visibility = seriesVisibilityRef.current;
      openSpreadSeries.applyOptions({ visible: !isMid && visibility.openSpread });
      closeSpreadSeries.applyOptions({ visible: !isMid && visibility.closeSpread });
      openLineSeries.applyOptions({ visible: !isMid && visibility.openLine });
      closeLineSeries.applyOptions({ visible: !isMid && visibility.closeLine });
      midSpreadSeries.applyOptions({ visible: isMid && visibility.midSpread });
      medianMidSpreadSeries.applyOptions({ visible: isMid && visibility.medianMidSpread });
    }

    if (paneLayoutRef.current.visible[1] ?? true) {
      const showCandle = klineDisplayModeRef.current === 'candle';
      const showCloseLine = klineDisplayModeRef.current === 'close-line';
      const klineVisibility = seriesVisibilityRef.current;
      exchange1Series.applyOptions({ visible: showCandle && klineVisibility.klineExchange1 });
      exchange2Series.applyOptions({ visible: showCandle && klineVisibility.klineExchange2 });
      exchange1CloseLineSeries.applyOptions({ visible: showCloseLine && klineVisibility.klineExchange1 });
      exchange2CloseLineSeries.applyOptions({ visible: showCloseLine && klineVisibility.klineExchange2 });
    }

    if (paneLayoutRef.current.visible[2] ?? true) {
      const fundingVisibility = seriesVisibilityRef.current;
      exchange1FundingSeries.applyOptions({ visible: fundingVisibility.fundingExchange1 });
      exchange2FundingSeries.applyOptions({ visible: fundingVisibility.fundingExchange2 });
      netFundingSettlementSeries.applyOptions({ visible: fundingVisibility.netFunding });
    }

    reapplyInstructionThresholdPriceLines(
      openSpreadSeries,
      closeSpreadSeries,
      spreadViewModeRef.current === 'orderbook' ? instructionThresholdLinesRef.current : [],
      chartSpreadScaleRef.current,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeries,
      spreadViewModeRef.current === 'orderbook' ? positionOpenSpreadLineRef.current : null,
      chartSpreadScaleRef.current,
      positionOpenSpreadPriceLineRef,
    );

    const detachRangeMeasure = attachChartRangeMeasure(container, chart, {
      panes: [
        {
          paneIndex: 0,
          measureMode: 'spread',
          spread: {
            openLabel: 'Open Spread',
            closeLabel: 'Close Spread',
            chartSpreadScale: 1,
            formatValue: formatSpreadFundingValue,
            getOpenSpread: () =>
              spreadViewModeRef.current === 'mid' ? midSpreadDataRef.current : openSpreadDataRef.current,
            getCloseSpread: () =>
              spreadViewModeRef.current === 'mid' ? medianMidSpreadDataRef.current : closeSpreadDataRef.current,
          },
          series: [],
          funding: {
            getDirection: () => directionRef.current,
            getExchange1Label: () => exchange1LabelRef.current,
            getExchange2Label: () => exchange2LabelRef.current,
            getExchange1Settlements: () =>
              alignSettlementsToFundingLine(
                exchange1FundingSettlementsRef.current,
                exchange1FundingDataRef.current,
                intervalSeconds,
              ),
            getExchange2Settlements: () =>
              alignSettlementsToFundingLine(
                exchange2FundingSettlementsRef.current,
                exchange2FundingDataRef.current,
                intervalSeconds,
              ),
            getExchange1Candles: () => exchange1DataRef.current,
            getExchange2Candles: () => exchange2DataRef.current,
          },
        },
        {
          paneIndex: 1,
          measureMode: 'kline',
          series: [
            {
              kind: 'candle',
              label: `${exchange1Label} K线`,
              color: '#16a34a',
              getData: () => exchange1DataRef.current,
            },
            {
              kind: 'candle',
              label: `${exchange2Label} K线`,
              color: '#0ea5e9',
              getData: () => exchange2DataRef.current,
            },
          ],
          funding: {
            getDirection: () => directionRef.current,
            getExchange1Label: () => exchange1LabelRef.current,
            getExchange2Label: () => exchange2LabelRef.current,
            getExchange1Settlements: () =>
              alignSettlementsToFundingLine(
                exchange1FundingSettlementsRef.current,
                exchange1FundingDataRef.current,
                intervalSeconds,
              ),
            getExchange2Settlements: () =>
              alignSettlementsToFundingLine(
                exchange2FundingSettlementsRef.current,
                exchange2FundingDataRef.current,
                intervalSeconds,
              ),
            getExchange1Candles: () => exchange1DataRef.current,
            getExchange2Candles: () => exchange2DataRef.current,
          },
        },
      ],
    });

    const resizeObserver = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth || 800, height: chartHeightRef.current });
      applyPaneStretchFactors(chart, paneLayoutRef.current);
    });
    resizeObserver.observe(container);

    return () => {
      cycleOverlayRef.current?.dispose();
      cycleOverlayRef.current = null;
      detachRangeMeasure();
      pointerTracker.dispose();
      resizeObserver.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
      chart.remove();
      chartRef.current = null;
      openSpreadSeriesRef.current = null;
      closeSpreadSeriesRef.current = null;
      openLineSeriesRef.current = null;
      closeLineSeriesRef.current = null;
      midSpreadSeriesRef.current = null;
      medianMidSpreadSeriesRef.current = null;
      exchange1SeriesRef.current = null;
      exchange2SeriesRef.current = null;
      exchange1CloseLineSeriesRef.current = null;
      exchange2CloseLineSeriesRef.current = null;
      exchange1FundingSeriesRef.current = null;
      exchange2FundingSeriesRef.current = null;
      netFundingSettlementSeriesRef.current = null;
      exchange1FundingMarkersRef.current?.detach();
      exchange1FundingMarkersRef.current = null;
      exchange2FundingMarkersRef.current?.detach();
      exchange2FundingMarkersRef.current = null;
      spreadCycleMarkersRef.current?.detach();
      spreadCycleMarkersRef.current = null;
      spreadCycleMarkerTargetRef.current = null;
      clearInstructionThresholdPriceLines(instructionThresholdPriceLinesRef);
      clearPositionOpenSpreadPriceLine(positionOpenSpreadPriceLineRef);
      shouldFollowLatestRef.current = true;
      latestLogicalRef.current = null;
      didFitInitialRef.current = false;
    };
  }, [direction, exchange1Label, exchange2Label]);

  useEffect(() => {
    chartRef.current?.applyOptions({ height: chartHeight });
  }, [chartHeight]);

  useEffect(() => {
    exchange1FundingMarkersRef.current?.setMarkers(
      seriesVisibility.fundingExchange1
        ? toFundingSettlementMarkers(
            alignSettlementsToFundingLine(
              exchange1FundingSettlements,
              exchange1FundingData,
              intervalSeconds,
            ),
            '#16a34a',
          )
        : [],
    );
    exchange2FundingMarkersRef.current?.setMarkers(
      seriesVisibility.fundingExchange2
        ? toFundingSettlementMarkers(
            alignSettlementsToFundingLine(
              exchange2FundingSettlements,
              exchange2FundingData,
              intervalSeconds,
            ),
            '#0ea5e9',
          )
        : [],
    );
  }, [
    exchange1FundingData,
    exchange1FundingSettlements,
    exchange2FundingData,
    exchange2FundingSettlements,
    intervalSeconds,
    seriesVisibility.fundingExchange1,
    seriesVisibility.fundingExchange2,
  ]);

  useEffect(() => {
    const isMidView = spreadViewMode === 'mid';
    const markerSeries = isMidView ? midSpreadData : openSpreadData;
    const markerTargetSeries = isMidView ? midSpreadSeriesRef.current : openSpreadSeriesRef.current;
    const lastPoint = markerSeries[markerSeries.length - 1];
    lastSpreadTimeMsRef.current = lastPoint != null ? Number(lastPoint.time) * 1000 : null;

    const markers =
      !showDeviationCycles || !deviationCycles
        ? []
        : buildSpreadDeviationCycleMarkers(
            deviationCycles.cycles,
            deviationCycles.open_cycle,
            intervalSeconds,
            markerSeries,
            { includePeakMarker: isMidView },
          );

    if (markerTargetSeries) {
      if (spreadCycleMarkerTargetRef.current !== markerTargetSeries) {
        spreadCycleMarkersRef.current?.detach();
        spreadCycleMarkersRef.current = createSeriesMarkers(markerTargetSeries, markers);
        spreadCycleMarkerTargetRef.current = markerTargetSeries;
      } else {
        spreadCycleMarkersRef.current?.setMarkers(markers);
      }
    } else {
      spreadCycleMarkersRef.current?.setMarkers([]);
    }

    cycleOverlayRef.current?.render();
  }, [
    deviationCycles,
    intervalSeconds,
    midSpreadData,
    openSpreadData,
    showDeviationCycles,
    spreadViewMode,
  ]);

  useEffect(() => {
    if (paneLayout.visible[0] ?? true) {
      const isMid = spreadViewMode === 'mid';
      openSpreadSeriesRef.current?.applyOptions({ visible: !isMid && seriesVisibility.openSpread });
      closeSpreadSeriesRef.current?.applyOptions({ visible: !isMid && seriesVisibility.closeSpread });
      openLineSeriesRef.current?.applyOptions({ visible: !isMid && seriesVisibility.openLine });
      closeLineSeriesRef.current?.applyOptions({ visible: !isMid && seriesVisibility.closeLine });
      midSpreadSeriesRef.current?.applyOptions({ visible: isMid && seriesVisibility.midSpread });
      medianMidSpreadSeriesRef.current?.applyOptions({ visible: isMid && seriesVisibility.medianMidSpread });
    }
    if (paneLayout.visible[1] ?? true) {
      const showCandle = klineDisplayMode === 'candle';
      const showCloseLine = klineDisplayMode === 'close-line';
      exchange1SeriesRef.current?.applyOptions({ visible: showCandle && seriesVisibility.klineExchange1 });
      exchange2SeriesRef.current?.applyOptions({ visible: showCandle && seriesVisibility.klineExchange2 });
      exchange1CloseLineSeriesRef.current?.applyOptions({ visible: showCloseLine && seriesVisibility.klineExchange1 });
      exchange2CloseLineSeriesRef.current?.applyOptions({ visible: showCloseLine && seriesVisibility.klineExchange2 });
    }
    if (paneLayout.visible[2] ?? true) {
      exchange1FundingSeriesRef.current?.applyOptions({ visible: seriesVisibility.fundingExchange1 });
      exchange2FundingSeriesRef.current?.applyOptions({ visible: seriesVisibility.fundingExchange2 });
      netFundingSettlementSeriesRef.current?.applyOptions({ visible: seriesVisibility.netFunding });
    }
  }, [direction, exchange1Label, exchange2Label, klineDisplayMode, paneLayout.visible, seriesVisibility, spreadViewMode]);

  useEffect(() => {
    openSpreadSeriesRef.current?.setData(openSpreadData);
    closeSpreadSeriesRef.current?.setData(closeSpreadData);
    openLineSeriesRef.current?.setData(openLineData);
    closeLineSeriesRef.current?.setData(closeLineData);
    midSpreadSeriesRef.current?.setData(midSpreadData);
    medianMidSpreadSeriesRef.current?.setData(medianMidSpreadData);
    exchange1SeriesRef.current?.setData(exchange1Data);
    exchange2SeriesRef.current?.setData(exchange2Data);
    exchange1CloseLineSeriesRef.current?.setData(mapCandlesToCloseLine(exchange1Data));
    exchange2CloseLineSeriesRef.current?.setData(mapCandlesToCloseLine(exchange2Data));
    exchange1FundingSeriesRef.current?.setData(exchange1FundingData);
    exchange2FundingSeriesRef.current?.setData(exchange2FundingData);
    netFundingSettlementSeriesRef.current?.setData(mapNetFundingToBarData(netFundingSettlementData));
    reapplyInstructionThresholdPriceLines(
      openSpreadSeriesRef.current,
      closeSpreadSeriesRef.current,
      spreadViewModeRef.current === 'orderbook' ? instructionThresholdLinesRef.current : [],
      chartSpreadScaleRef.current,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeriesRef.current,
      spreadViewModeRef.current === 'orderbook' ? positionOpenSpreadLineRef.current : null,
      chartSpreadScaleRef.current,
      positionOpenSpreadPriceLineRef,
    );
    const maxPoints = Math.max(
      openSpreadData.length,
      closeSpreadData.length,
      openLineData.length,
      closeLineData.length,
      midSpreadData.length,
      medianMidSpreadData.length,
      exchange1Data.length,
      exchange2Data.length,
      exchange1FundingData.length,
      exchange2FundingData.length,
    );
    if (maxPoints === 0) return;
    if (!didFitInitialRef.current) {
      chartRef.current?.timeScale().fitContent();
      didFitInitialRef.current = true;
    } else if (shouldFollowLatestRef.current) {
      latestLogicalRef.current = maxPoints - 1;
      chartRef.current?.timeScale().scrollToRealTime();
    }
  }, [
    closeLineData,
    closeSpreadData,
    exchange1Data,
    exchange1FundingData,
    exchange2Data,
    exchange2FundingData,
    midSpreadData,
    medianMidSpreadData,
    netFundingSettlementData,
    openLineData,
    openSpreadData,
  ]);

  useEffect(() => {
    const lines = spreadViewMode === 'orderbook' ? instructionThresholdLines : [];
    reapplyInstructionThresholdPriceLines(
      openSpreadSeriesRef.current,
      closeSpreadSeriesRef.current,
      lines,
      chartSpreadScale,
      instructionThresholdPriceLinesRef,
    );
    reapplyPositionOpenSpreadPriceLine(
      openSpreadSeriesRef.current,
      spreadViewMode === 'orderbook' ? positionOpenSpreadLine : null,
      chartSpreadScale,
      positionOpenSpreadPriceLineRef,
    );
  }, [chartSpreadScale, instructionThresholdLines, positionOpenSpreadLine, spreadViewMode]);

  return (
    <div className="realtime-spread-chart-wrap">
      <div
        className="realtime-spread-chart is-spread-price"
        ref={containerRef}
        style={{ height: chartHeight, minHeight: chartHeight }}
      />
      <InstructionThresholdAxisOverlay
        chartRef={chartRef}
        chartContainerRef={containerRef}
        openSpreadSeriesRef={openSpreadSeriesRef}
        closeSpreadSeriesRef={closeSpreadSeriesRef}
        lines={instructionThresholdLines}
        instructions={pairInstructions}
        chartSpreadScale={chartSpreadScale}
        enabled={spreadViewMode === 'orderbook'}
      />
      <TradeVolumeChartOverlay
        chartRef={chartRef}
        chartContainerRef={containerRef}
        exchange1={exchange1Label}
        exchange2={exchange2Label}
        exchange1InstId={exchange1InstId}
        exchange2InstId={exchange2InstId}
        enabled={showTradeVolumeStrip}
        klinePaneCollapsed={!(paneLayout.visible[1] ?? true)}
        chartRevision={chartRevision}
      />
      {showFundingStrip ? (
        <FundingChartOverlay
          chartRef={chartRef}
          chartContainerRef={containerRef}
          enabled={showFundingStrip}
          fundingPaneCollapsed={!(paneLayout.visible[2] ?? true)}
          chartRevision={chartRevision}
          {...fundingStrip}
        />
      ) : null}
    </div>
  );
}

export type RealtimeSpreadHistorySource = 'server-py' | 'opp-detector';
export type RealtimeSpreadChartMode = 'full' | 'spread-price';
type ChartSectionTab = 'chart' | 'datadetail' | 'basicinfo';

export interface RealtimeSpreadProps {
  historySource?: RealtimeSpreadHistorySource;
  chartMode?: RealtimeSpreadChartMode;
}

export function RealtimeSpread({
  historySource = 'server-py',
  chartMode = 'full',
}: RealtimeSpreadProps) {
  const { user, isTradingSessionReady } = useAuth();
  const isSpreadPriceMode = chartMode === 'spread-price';
  const [searchParams] = useSearchParams();
  const isOppDetectorSource = historySource === 'opp-detector';
  const [config, setConfig] = useState<RealtimeSpreadConfig>(() => {
    const initialSymbol = 'BTC';
    const exchange1 = 'OKX';
    const exchange2 = 'Bybit';
    const generated = generateExchangeInstIds(exchange1, exchange2, initialSymbol);
    return {
      symbol: initialSymbol,
      exchange1,
      exchange2,
      instId1: generated.instId1,
      instId2: generated.instId2,
      interval: 60,
      direction: 'long1_short2',
      smaPeriod: DEFAULT_SMA_PERIOD,
      exchange1Fee: OPP_DETECTOR_DEFAULT_EXCHANGE1_FEE,
      exchange2Fee: OPP_DETECTOR_DEFAULT_EXCHANGE2_FEE,
      minProfitPercentage: OPP_DETECTOR_DEFAULT_MIN_PROFIT_PERCENTAGE,
    };
  });
  const [startTime, setStartTime] = useState(() => defaultStartForInterval(DEFAULT_INTERVAL_SECONDS));
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [spreadSeriesData, setSpreadSeriesData] = useState<SpreadSeriesData>(() => emptySpreadSeriesData());
  const [biasExchange1Data, setBiasExchange1Data] = useState<SpreadPoint[]>([]);
  const [biasExchange2Data, setBiasExchange2Data] = useState<SpreadPoint[]>([]);
  const [metricSeriesData, setMetricSeriesData] = useState<MetricSeriesData>(() => emptyMetricSeriesData());
  const [exchange1KlineData, setExchange1KlineData] = useState<CandlePoint[]>([]);
  const [exchange2KlineData, setExchange2KlineData] = useState<CandlePoint[]>([]);
  const [exchange1FundingData, setExchange1FundingData] = useState<SpreadPoint[]>([]);
  const [exchange2FundingData, setExchange2FundingData] = useState<SpreadPoint[]>([]);
  const [exchange1FundingSettlements, setExchange1FundingSettlements] = useState<SpreadPoint[]>([]);
  const [exchange2FundingSettlements, setExchange2FundingSettlements] = useState<SpreadPoint[]>([]);
  const [fundingSummary, setFundingSummary] = useState<{
    exchange1CurrentRate: number | null;
    exchange2CurrentRate: number | null;
    exchange1NextFundingTimeMs: number | null;
    exchange2NextFundingTimeMs: number | null;
  }>({
    exchange1CurrentRate: null,
    exchange2CurrentRate: null,
    exchange1NextFundingTimeMs: null,
    exchange2NextFundingTimeMs: null,
  });
  const [fundingInstrumentMeta, setFundingInstrumentMeta] = useState<{
    exchange1: FundingInstrumentMeta | null;
    exchange2: FundingInstrumentMeta | null;
  }>({
    exchange1: null,
    exchange2: null,
  });
  const [symbolCategory, setSymbolCategory] = useState<string | null>(null);
  const [symbolCategoryLoaded, setSymbolCategoryLoaded] = useState(false);
  const [indexComponentsMeta, setIndexComponentsMeta] = useState<{
    exchange1: Record<string, unknown> | null;
    exchange2: Record<string, unknown> | null;
  }>({
    exchange1: null,
    exchange2: null,
  });
  const [seriesVisibility, setSeriesVisibility] = useState<ChartSeriesVisibility>(() => defaultChartSeriesVisibility());
  const [streamStatus, setStreamStatus] = useState<StreamStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [pendingAutoStart, setPendingAutoStart] = useState(false);
  const [selectedPositionGroupId, setSelectedPositionGroupId] = useState<string | null>(null);
  const [chartLinkedPositionGroup, setChartLinkedPositionGroup] = useState<PositionGroupView | null>(null);
  const [positionGroupsRevision, setPositionGroupsRevision] = useState(0);
  const [fundingNowMs, setFundingNowMs] = useState(() => Date.now());
  const [fundingSummaryUpdatedAtMs, setFundingSummaryUpdatedAtMs] = useState<number | null>(null);
  const [fundingRefreshLoading, setFundingRefreshLoading] = useState(false);
  const [fundingRefreshError, setFundingRefreshError] = useState<string | null>(null);
  const chartHeightStorageKey = isSpreadPriceMode
    ? CHART_HEIGHT_STORAGE_KEY_SPREAD_PRICE
    : CHART_HEIGHT_STORAGE_KEY_FULL;
  const defaultChartViewportHeight = isSpreadPriceMode
    ? DEFAULT_CHART_HEIGHT_SPREAD_PRICE
    : DEFAULT_CHART_HEIGHT_FULL;
  const [chartViewportHeight, setChartViewportHeight] = useState(() =>
    loadChartViewportHeight(chartHeightStorageKey, defaultChartViewportHeight),
  );
  const paneLayoutStorageKey = isSpreadPriceMode
    ? PANE_LAYOUT_STORAGE_KEY_SPREAD
    : PANE_LAYOUT_STORAGE_KEY_FULL;
  const paneLabels = isSpreadPriceMode ? SPREAD_PRICE_PANE_LABELS : FULL_PANE_LABELS;
  const [paneLayout, setPaneLayout] = useState<ChartPaneLayout>(() =>
    loadChartPaneLayout(paneLayoutStorageKey, isSpreadPriceMode),
  );
  const [spreadDetailSettingsOpen, setSpreadDetailSettingsOpen] = useState(false);
  const [spreadViewMode, setSpreadViewMode] = useState<DetailedSpreadViewMode>('orderbook');
  const [klineDisplayMode, setKlineDisplayMode] = useState<KlineDisplayMode>(() => loadKlineDisplayMode());
  const [oppPriceDataSource, setOppPriceDataSource] = useState<OppSpreadPriceDataSource>(() =>
    loadOppPriceDataSource(),
  );
  const [deviationCycles, setDeviationCycles] = useState<OppSpreadDeviationCyclesResponse | null>(null);
  const [pairInstructions, setPairInstructions] = useState<InstructionView[]>([]);
  const [deviationCycleConfig, setDeviationCycleConfig] = useState<OppDeviationCycleConfig>(() =>
    defaultOppDeviationCycleConfig(),
  );
  const [deviationCycleSettingsOpen, setDeviationCycleSettingsOpen] = useState(false);
  const [deviationCycleConfigError, setDeviationCycleConfigError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const spreadDetailSettingsRef = useRef<HTMLDivElement | null>(null);
  const deviationCycleSettingsRef = useRef<HTMLDivElement | null>(null);
  const mainColumnRef = useRef<HTMLDivElement | null>(null);
  const chartSectionRef = useRef<HTMLDivElement | null>(null);
  const [accountSummaryHost, setAccountSummaryHost] = useState<HTMLElement | null>(null);
  const [chartSectionTab, setChartSectionTab] = useState<ChartSectionTab>('chart');
  const reconnectTimerRef = useRef<number | null>(null);
  const backoffMsRef = useRef(1000);
  const sessionIdRef = useRef(0);
  const historyQueryStartMsRef = useRef<number | null>(null);
  const positionGroupsRef = useRef<PositionGroupView[]>([]);
  const explicitPositionSelectionRef = useRef(false);
  const configTouchedByUserRef = useRef(false);
  const didAutoLoadLargestPositionRef = useRef(false);
  const lastSpreadTsRef = useRef<number | null>(null);
  const lastCandleTsRef = useRef<number | null>(null);
  const pendingRef = useRef<PendingUpdates>(emptyPendingUpdates());
  const handledSearchRef = useRef<string>('');
  const configRef = useRef(config);
  configRef.current = config;
  const oppPriceDataSourceRef = useRef(oppPriceDataSource);
  oppPriceDataSourceRef.current = oppPriceDataSource;
  const isTradePriceDataSource = isOppDetectorSource && isSpreadPriceMode && oppPriceDataSource === 'trade';
  const deviationCycleConfigRef = useRef(deviationCycleConfig);
  deviationCycleConfigRef.current = deviationCycleConfig;
  const startTimeRef = useRef(startTime);
  const startTimeUserChangeRef = useRef(false);
  startTimeRef.current = startTime;

  const symbol = normalizeSymbolInput(config.symbol);
  const inst1 = config.instId1.trim();
  const inst2 = config.instId2.trim();
  const instructionPairQuery = useMemo(
    () => buildInstructionExchangePairQuery(symbol, config.exchange1, config.exchange2),
    [symbol, config.exchange1, config.exchange2],
  );
  const instructionThresholdLines = useMemo(
    () =>
      selectActiveInstructionThresholds(
        filterInstructionsForChartSpreadDirection(
          pairInstructions,
          config.exchange1,
          config.exchange2,
          config.direction,
        ),
      ),
    [pairInstructions, config.exchange1, config.exchange2, config.direction],
  );
  const positionOpenSpreadLine = useMemo(
    () =>
      resolvePositionOpenSpreadChartLine(
        chartLinkedPositionGroup,
        symbol,
        config.exchange1,
        config.exchange2,
        config.direction,
      ),
    [
      chartLinkedPositionGroup,
      symbol,
      config.exchange1,
      config.exchange2,
      config.direction,
    ],
  );
  const chartSpreadScale = isSpreadPriceMode ? 1 : 100;
  const latestOpenLineByDirection = useMemo<LatestOpenLineByDirection>(
    () => ({
      long1_short2: getLatestChartSeriesRawValue(
        spreadSeriesData.long1Short2OpenLine,
        chartSpreadScale,
      ),
      short1_long2: getLatestChartSeriesRawValue(
        spreadSeriesData.short1Long2OpenLine,
        chartSpreadScale,
      ),
    }),
    [spreadSeriesData.long1Short2OpenLine, spreadSeriesData.short1Long2OpenLine, chartSpreadScale],
  );
  const latestOpenLineForDirection = latestOpenLineByDirection[config.direction];
  const selectedOpenSpreadData =
    config.direction === 'long1_short2' ? spreadSeriesData.long1Short2Open : spreadSeriesData.short1Long2Open;
  const selectedCloseSpreadData =
    config.direction === 'long1_short2' ? spreadSeriesData.long1Short2Close : spreadSeriesData.short1Long2Close;
  const selectedOpenLineData =
    config.direction === 'long1_short2' ? spreadSeriesData.long1Short2OpenLine : spreadSeriesData.short1Long2OpenLine;
  const selectedCloseLineData =
    config.direction === 'long1_short2' ? spreadSeriesData.long1Short2CloseLine : spreadSeriesData.short1Long2CloseLine;
  const selectedMidSpreadData =
    config.direction === 'long1_short2' ? spreadSeriesData.long1Short2MidSpread : spreadSeriesData.short1Long2MidSpread;
  const selectedMedianMidSpreadData =
    config.direction === 'long1_short2'
      ? spreadSeriesData.long1Short2MedianMidSpread
      : spreadSeriesData.short1Long2MedianMidSpread;
  const selectedIndexSpreadData =
    config.direction === 'long1_short2'
      ? metricSeriesData.long1Short2IndexSpread
      : metricSeriesData.short1Long2IndexSpread;
  const selectedAdjustedSpreadMaData = useMemo<OffsetMaSeriesData>(
    () =>
      config.direction === 'long1_short2'
        ? {
            ma1m: metricSeriesData.long1Short2AdjustedSpreadMa1m,
            ma5m: metricSeriesData.long1Short2AdjustedSpreadMa5m,
            ma10m: metricSeriesData.long1Short2AdjustedSpreadMa10m,
            ma60m: metricSeriesData.long1Short2AdjustedSpreadMa60m,
          }
        : {
            ma1m: metricSeriesData.short1Long2AdjustedSpreadMa1m,
            ma5m: metricSeriesData.short1Long2AdjustedSpreadMa5m,
            ma10m: metricSeriesData.short1Long2AdjustedSpreadMa10m,
            ma60m: metricSeriesData.short1Long2AdjustedSpreadMa60m,
          },
    [config.direction, metricSeriesData],
  );
  const netFundingSettlementData = useMemo(
    () =>
      buildNetFundingSettlementLine(
        config.direction,
        alignSettlementsToFundingLine(
          exchange1FundingSettlements,
          exchange1FundingData,
          config.interval,
        ),
        alignSettlementsToFundingLine(
          exchange2FundingSettlements,
          exchange2FundingData,
          config.interval,
        ),
        config.interval,
      ),
    [
      config.direction,
      config.interval,
      exchange1FundingData,
      exchange1FundingSettlements,
      exchange2FundingData,
      exchange2FundingSettlements,
    ],
  );
  const toggleSeriesVisibility = useCallback((key: keyof ChartSeriesVisibility) => {
    setSeriesVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const syncChartLinkedPositionGroup = useCallback(
    (groups: PositionGroupView[], configSnapshot: RealtimeSpreadConfig) => {
      const nextSymbol = normalizeSymbolInput(configSnapshot.symbol);
      if (explicitPositionSelectionRef.current && selectedPositionGroupId) {
        const selected =
          groups.find((group) => group.group_id === selectedPositionGroupId) ?? null;
        setChartLinkedPositionGroup(selected);
        return;
      }
      const matched = findMatchingPositionGroupForChart(
        groups,
        nextSymbol,
        configSnapshot.exchange1,
        configSnapshot.exchange2,
        configSnapshot.direction,
      );
      setChartLinkedPositionGroup(matched);
      setSelectedPositionGroupId(matched?.group_id ?? null);
    },
    [selectedPositionGroupId],
  );

  const handlePositionGroupsUpdate = useCallback((groups: PositionGroupView[]) => {
    positionGroupsRef.current = groups;
    if (!didAutoLoadLargestPositionRef.current) {
      setPositionGroupsRevision((revision) => revision + 1);
    }
    syncChartLinkedPositionGroup(groups, configRef.current);
  }, [syncChartLinkedPositionGroup]);

  useEffect(() => {
    syncChartLinkedPositionGroup(positionGroupsRef.current, config);
  }, [config.direction, config.exchange1, config.exchange2, config.symbol, syncChartLinkedPositionGroup]);

  useEffect(() => {
    const loadExchanges = historySource === 'opp-detector' ? getOppOrderbookExchanges : getExchanges;
    loadExchanges()
      .then((res) => setExchanges(Array.isArray(res) ? res : (res.exchanges ?? [])))
      .catch(() => setExchanges([]));
  }, [historySource]);

  useEffect(() => {
    if (
      !isManualTradingConfigured() ||
      !instructionPairQuery ||
      !isTradingSessionReady ||
      !user ||
      streamStatus === 'history_loading'
    ) {
      if (!isManualTradingConfigured() || !instructionPairQuery || !user) {
        setPairInstructions([]);
      }
      return undefined;
    }

    let cancelled = false;
    const pairQuery = instructionPairQuery;

    const loadInstructions = async () => {
      const rows = await listInstructionsIfIdle(pairQuery);
      if (rows === null || cancelled) return;
      setPairInstructions(rows);
    };

    const { run: scheduleLoad } = createSerialLoader(async () => {
      try {
        await loadInstructions();
      } catch {
        if (!cancelled) setPairInstructions([]);
      }
    });

    scheduleLoad();
    const timer = window.setInterval(() => scheduleLoad(), 10_000);
    const onInstructionsChanged = () => {
      scheduleLoad({ coalesce: true });
    };
    window.addEventListener(INSTRUCTIONS_CHANGED_EVENT, onInstructionsChanged);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(INSTRUCTIONS_CHANGED_EVENT, onInstructionsChanged);
    };
  }, [instructionPairQuery, isTradingSessionReady, user, streamStatus]);

  useEffect(() => {
    if (!isSpreadPriceMode || !isOppDetectorSource || !inst1 || !inst2) {
      setFundingInstrumentMeta({ exchange1: null, exchange2: null });
      setSymbolCategory(null);
      setSymbolCategoryLoaded(false);
      setIndexComponentsMeta({ exchange1: null, exchange2: null });
      return;
    }

    let cancelled = false;
    setSymbolCategoryLoaded(false);
    setFundingInstrumentMeta({ exchange1: null, exchange2: null });
    setIndexComponentsMeta({ exchange1: null, exchange2: null });
    void Promise.all([
      getOppInstrumentInfo(config.exchange1, inst1),
      getOppInstrumentInfo(config.exchange2, inst2),
    ])
      .then(([info1, info2]) => {
        if (cancelled) return;
        setFundingInstrumentMeta({
          exchange1: info1
            ? {
                fundingIntervalHours: normalizeFundingIntervalHours(info1.funding_interval_hours),
                upperFundingRate: info1.upper_funding_rate ?? null,
                lowerFundingRate: info1.lower_funding_rate ?? null,
              }
            : null,
          exchange2: info2
            ? {
                fundingIntervalHours: normalizeFundingIntervalHours(info2.funding_interval_hours),
                upperFundingRate: info2.upper_funding_rate ?? null,
                lowerFundingRate: info2.lower_funding_rate ?? null,
              }
            : null,
        });
        const merged = mergePairCategory(info1?.category, info2?.category);
        setSymbolCategory(merged || null);
        setIndexComponentsMeta({
          exchange1: info1?.index_components ?? null,
          exchange2: info2?.index_components ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFundingInstrumentMeta({ exchange1: null, exchange2: null });
          setSymbolCategory(null);
          setIndexComponentsMeta({ exchange1: null, exchange2: null });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSymbolCategoryLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [config.exchange1, config.exchange2, config.symbol, inst1, inst2, isOppDetectorSource, isSpreadPriceMode]);

  useEffect(() => {
    const queryKey = searchParams.toString();
    if (!queryKey || handledSearchRef.current === queryKey) return;

    const symbolParam = searchParams.get('symbol')?.trim() ?? '';
    const exchange1Param = searchParams.get('exchange1')?.trim() ?? '';
    const exchange2Param = searchParams.get('exchange2')?.trim() ?? '';
    const instId1Param = searchParams.get('instId1')?.trim() ?? '';
    const instId2Param = searchParams.get('instId2')?.trim() ?? '';
    const autoQueryParam = searchParams.get('autoQuery')?.trim().toLowerCase();

    handledSearchRef.current = queryKey;
    if (!symbolParam && !exchange1Param && !exchange2Param && !instId1Param && !instId2Param) return;

    setConfig((prev) => {
      const nextSymbol = symbolParam ? normalizeSymbolInput(symbolParam) : prev.symbol;
      const nextExchange1 = exchange1Param ? toUiExchange(exchange1Param) : prev.exchange1;
      const nextExchange2 = exchange2Param ? toUiExchange(exchange2Param) : prev.exchange2;
      const generated = generateExchangeInstIds(nextExchange1, nextExchange2, nextSymbol);
      return {
        ...prev,
        symbol: nextSymbol,
        exchange1: nextExchange1,
        exchange2: nextExchange2,
        instId1: instId1Param || generated.instId1,
        instId2: instId2Param || generated.instId2,
      };
    });

    if (autoQueryParam === '1' || autoQueryParam === 'true' || autoQueryParam === 'yes') {
      setPendingAutoStart(true);
    }
  }, [searchParams]);

  const resetConnection = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => () => resetConnection(), [resetConnection]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const pending = pendingRef.current;
      if (
        pending.long1Short2OpenSpread.length === 0 &&
        pending.long1Short2CloseSpread.length === 0 &&
        pending.short1Long2OpenSpread.length === 0 &&
        pending.short1Long2CloseSpread.length === 0 &&
        pending.biasExchange1.length === 0 &&
        pending.biasExchange2.length === 0 &&
        pending.long1Short2IndexSpread.length === 0 &&
        pending.short1Long2IndexSpread.length === 0 &&
        pending.long1Short2AdjustedSpreadMa1m.length === 0 &&
        pending.long1Short2AdjustedSpreadMa5m.length === 0 &&
        pending.long1Short2AdjustedSpreadMa10m.length === 0 &&
        pending.long1Short2AdjustedSpreadMa60m.length === 0 &&
        pending.short1Long2AdjustedSpreadMa1m.length === 0 &&
        pending.short1Long2AdjustedSpreadMa5m.length === 0 &&
        pending.short1Long2AdjustedSpreadMa10m.length === 0 &&
        pending.short1Long2AdjustedSpreadMa60m.length === 0 &&
        pending.exchange1Kline.length === 0 &&
        pending.exchange2Kline.length === 0
      ) {
        return;
      }
      const long1Short2OpenBatch = pending.long1Short2OpenSpread.splice(0, pending.long1Short2OpenSpread.length);
      const long1Short2CloseBatch = pending.long1Short2CloseSpread.splice(0, pending.long1Short2CloseSpread.length);
      const short1Long2OpenBatch = pending.short1Long2OpenSpread.splice(0, pending.short1Long2OpenSpread.length);
      const short1Long2CloseBatch = pending.short1Long2CloseSpread.splice(0, pending.short1Long2CloseSpread.length);
      const biasExchange1Batch = pending.biasExchange1.splice(0, pending.biasExchange1.length);
      const biasExchange2Batch = pending.biasExchange2.splice(0, pending.biasExchange2.length);
      const long1Short2IndexBatch = pending.long1Short2IndexSpread.splice(0, pending.long1Short2IndexSpread.length);
      const short1Long2IndexBatch = pending.short1Long2IndexSpread.splice(0, pending.short1Long2IndexSpread.length);
      const long1Short2AdjustedMa1mBatch = pending.long1Short2AdjustedSpreadMa1m.splice(0, pending.long1Short2AdjustedSpreadMa1m.length);
      const long1Short2AdjustedMa5mBatch = pending.long1Short2AdjustedSpreadMa5m.splice(0, pending.long1Short2AdjustedSpreadMa5m.length);
      const long1Short2AdjustedMa10mBatch = pending.long1Short2AdjustedSpreadMa10m.splice(0, pending.long1Short2AdjustedSpreadMa10m.length);
      const long1Short2AdjustedMa60mBatch = pending.long1Short2AdjustedSpreadMa60m.splice(0, pending.long1Short2AdjustedSpreadMa60m.length);
      const short1Long2AdjustedMa1mBatch = pending.short1Long2AdjustedSpreadMa1m.splice(0, pending.short1Long2AdjustedSpreadMa1m.length);
      const short1Long2AdjustedMa5mBatch = pending.short1Long2AdjustedSpreadMa5m.splice(0, pending.short1Long2AdjustedSpreadMa5m.length);
      const short1Long2AdjustedMa10mBatch = pending.short1Long2AdjustedSpreadMa10m.splice(0, pending.short1Long2AdjustedSpreadMa10m.length);
      const short1Long2AdjustedMa60mBatch = pending.short1Long2AdjustedSpreadMa60m.splice(0, pending.short1Long2AdjustedSpreadMa60m.length);
      const ex1Batch = pending.exchange1Kline.splice(0, pending.exchange1Kline.length);
      const ex2Batch = pending.exchange2Kline.splice(0, pending.exchange2Kline.length);

      if (
        long1Short2OpenBatch.length > 0 ||
        long1Short2CloseBatch.length > 0 ||
        short1Long2OpenBatch.length > 0 ||
        short1Long2CloseBatch.length > 0
      ) {
        setSpreadSeriesData((prev) => ({
          long1Short2Open: long1Short2OpenBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2Open),
          long1Short2Close: long1Short2CloseBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2Close),
          short1Long2Open: short1Long2OpenBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2Open),
          short1Long2Close: short1Long2CloseBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2Close),
          long1Short2OpenLine: prev.long1Short2OpenLine,
          long1Short2CloseLine: prev.long1Short2CloseLine,
          short1Long2OpenLine: prev.short1Long2OpenLine,
          short1Long2CloseLine: prev.short1Long2CloseLine,
          long1Short2MidSpread: prev.long1Short2MidSpread,
          short1Long2MidSpread: prev.short1Long2MidSpread,
          long1Short2MedianMidSpread: prev.long1Short2MedianMidSpread,
          short1Long2MedianMidSpread: prev.short1Long2MedianMidSpread,
        }));
      }
      if (biasExchange1Batch.length > 0) {
        setBiasExchange1Data((prev) => biasExchange1Batch.reduce((acc, point) => upsertLine(acc, point), prev));
      }
      if (biasExchange2Batch.length > 0) {
        setBiasExchange2Data((prev) => biasExchange2Batch.reduce((acc, point) => upsertLine(acc, point), prev));
      }
      if (
        long1Short2IndexBatch.length > 0 ||
        short1Long2IndexBatch.length > 0 ||
        long1Short2AdjustedMa1mBatch.length > 0 ||
        long1Short2AdjustedMa5mBatch.length > 0 ||
        long1Short2AdjustedMa10mBatch.length > 0 ||
        long1Short2AdjustedMa60mBatch.length > 0 ||
        short1Long2AdjustedMa1mBatch.length > 0 ||
        short1Long2AdjustedMa5mBatch.length > 0 ||
        short1Long2AdjustedMa10mBatch.length > 0 ||
        short1Long2AdjustedMa60mBatch.length > 0
      ) {
        setMetricSeriesData((prev) => ({
          long1Short2IndexSpread: long1Short2IndexBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2IndexSpread),
          short1Long2IndexSpread: short1Long2IndexBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2IndexSpread),
          long1Short2AdjustedSpreadMa1m: long1Short2AdjustedMa1mBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa1m),
          long1Short2AdjustedSpreadMa5m: long1Short2AdjustedMa5mBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa5m),
          long1Short2AdjustedSpreadMa10m: long1Short2AdjustedMa10mBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa10m),
          long1Short2AdjustedSpreadMa60m: long1Short2AdjustedMa60mBatch.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa60m),
          short1Long2AdjustedSpreadMa1m: short1Long2AdjustedMa1mBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa1m),
          short1Long2AdjustedSpreadMa5m: short1Long2AdjustedMa5mBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa5m),
          short1Long2AdjustedSpreadMa10m: short1Long2AdjustedMa10mBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa10m),
          short1Long2AdjustedSpreadMa60m: short1Long2AdjustedMa60mBatch.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa60m),
        }));
      }
      if (ex1Batch.length > 0) {
        setExchange1KlineData((prev) => ex1Batch.reduce((acc, point) => upsertCandle(acc, point), prev));
      }
      if (ex2Batch.length > 0) {
        setExchange2KlineData((prev) => ex2Batch.reduce((acc, point) => upsertCandle(acc, point), prev));
      }
    }, FLUSH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  const setField = (field: keyof RealtimeSpreadConfig) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;
    configTouchedByUserRef.current = true;
    setConfig((prev) => ({
      ...prev,
      [field]: event.target instanceof HTMLInputElement && event.target.type === 'number' ? Number(value) : value,
    }));
  };

  const intervalPreset = useMemo(() => getIntervalPreset(config.interval), [config.interval]);
  const startTimeBounds = useMemo(() => startTimeBoundsForInterval(config.interval), [config.interval]);

  const onStartTimeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      startTimeUserChangeRef.current = true;
      const next = clampStartTimeInput(event.target.value, config.interval);
      setStartTime(next);
      startTimeRef.current = next;
    },
    [config.interval],
  );

  type HistoryRangeOptions = {
    toIso: string;
    spreadFromIso?: string;
    candleFromIso?: string;
    fundingFromIso?: string;
    fetchSpread?: boolean;
    fetchCandles?: boolean;
    fetchFunding?: boolean;
  };

  const applyHistoryRange = useCallback(
    async (nextConfig: RealtimeSpreadConfig, options: HistoryRangeOptions) => {
      const fetchSpread = options.fetchSpread ?? true;
      const fetchCandles = options.fetchCandles ?? true;
      const fetchFunding = options.fetchFunding ?? (historySource === 'opp-detector' && isSpreadPriceMode);
      const spreadFromIso = options.spreadFromIso;
      const candleFromIso = options.candleFromIso ?? options.spreadFromIso;
      const fundingFromIso = options.fundingFromIso ?? options.spreadFromIso;
      const nextSymbol = normalizeSymbolInput(nextConfig.symbol);
      const nextInst1 = nextConfig.instId1.trim();
      const nextInst2 = nextConfig.instId2.trim();
      const indexExchange1 = toIndexExchange(nextConfig.exchange1);
      const indexExchange2 = toIndexExchange(nextConfig.exchange2);
      const endMs = new Date(options.toIso).getTime();
      const spreadStartMs = spreadFromIso ? new Date(spreadFromIso).getTime() : undefined;
      const candleStartMs = candleFromIso ? new Date(candleFromIso).getTime() : undefined;
      const fundingStartMs = fundingFromIso ? new Date(fundingFromIso).getTime() : undefined;

      const useTradePriceData =
        historySource === 'opp-detector' && isSpreadPriceMode && oppPriceDataSource === 'trade';

      const tradeSpreadPromise =
        fetchSpread && spreadFromIso && useTradePriceData
          ? getOppTradePriceSpread({
              exchange1: nextConfig.exchange1,
              inst_id1: nextInst1,
              exchange2: nextConfig.exchange2,
              inst_id2: nextInst2,
              start_ms: spreadStartMs!,
              end_ms: endMs,
              interval: nextConfig.interval,
            })
          : Promise.resolve({ interval_secs: nextConfig.interval, total_points: 0, data: [] });

      const detailedSpreadPromise =
        fetchSpread && spreadFromIso && !useTradePriceData
          ? historySource === 'opp-detector'
            ? getOppDetailedSpread({
                exchange1: nextConfig.exchange1,
                inst_id1: nextInst1,
                exchange2: nextConfig.exchange2,
                inst_id2: nextInst2,
                start_ms: spreadStartMs!,
                end_ms: endMs,
                interval: nextConfig.interval,
                sma_period: nextConfig.smaPeriod,
                exchange1_fee: nextConfig.exchange1Fee,
                exchange2_fee: nextConfig.exchange2Fee,
                min_profit_percentage: nextConfig.minProfitPercentage,
              })
            : getDetailedSpread({
                exchange1: nextConfig.exchange1,
                inst_id1: nextInst1,
                exchange2: nextConfig.exchange2,
                inst_id2: nextInst2,
                start_time: spreadFromIso,
                end_time: options.toIso,
                interval: nextConfig.interval,
              })
          : Promise.resolve({ data: [] as DetailedSpreadDataPoint[] });
      const candle1Promise =
        fetchCandles && candleFromIso
          ? useTradePriceData
            ? getOppTradeCandles({
                exchange: nextConfig.exchange1,
                inst_id: nextInst1,
                start_ms: candleStartMs!,
                end_ms: endMs,
                interval: nextConfig.interval,
              })
            : historySource === 'opp-detector'
            ? getOppL1Candles({
                exchange: nextConfig.exchange1,
                inst_id: nextInst1,
                start_ms: candleStartMs!,
                end_ms: endMs,
                interval: nextConfig.interval,
              })
            : getCandles({
                exchange: nextConfig.exchange1,
                inst_id: nextInst1,
                start_time: candleFromIso,
                end_time: options.toIso,
                interval: nextConfig.interval,
              })
          : Promise.resolve({ data: [] as CandleDataPoint[] });
      const candle2Promise =
        fetchCandles && candleFromIso
          ? useTradePriceData
            ? getOppTradeCandles({
                exchange: nextConfig.exchange2,
                inst_id: nextInst2,
                start_ms: candleStartMs!,
                end_ms: endMs,
                interval: nextConfig.interval,
              })
            : historySource === 'opp-detector'
            ? getOppL1Candles({
                exchange: nextConfig.exchange2,
                inst_id: nextInst2,
                start_ms: candleStartMs!,
                end_ms: endMs,
                interval: nextConfig.interval,
              })
            : getCandles({
                exchange: nextConfig.exchange2,
                inst_id: nextInst2,
                start_time: candleFromIso,
                end_time: options.toIso,
                interval: nextConfig.interval,
              })
          : Promise.resolve({ data: [] as CandleDataPoint[] });
      const funding1Promise =
        fetchFunding && fundingFromIso
          ? getOppFundingHistory({
              exchange: nextConfig.exchange1,
              inst_id: nextInst1,
              start_ms: fundingStartMs!,
              end_ms: endMs,
              interval: nextConfig.interval,
            })
          : Promise.resolve(null);
      const funding2Promise =
        fetchFunding && fundingFromIso
          ? getOppFundingHistory({
              exchange: nextConfig.exchange2,
              inst_id: nextInst2,
              start_ms: fundingStartMs!,
              end_ms: endMs,
              interval: nextConfig.interval,
            })
          : Promise.resolve(null);

      const cycleQueryStartMs = historyQueryStartMsRef.current ?? spreadStartMs;
      const shouldFetchDeviationCycles =
        isSpreadPriceMode &&
        historySource === 'opp-detector' &&
        !useTradePriceData &&
        fetchSpread &&
        spreadFromIso != null &&
        cycleQueryStartMs != null;
      const cycleConfig = deviationCycleConfigRef.current;
      const deviationCyclesPromise = shouldFetchDeviationCycles
        ? getOppSpreadDeviationCycles({
            exchange1: nextConfig.exchange1,
            inst_id1: nextInst1,
            exchange2: nextConfig.exchange2,
            inst_id2: nextInst2,
            start_ms: cycleQueryStartMs,
            end_ms: endMs,
            interval: nextConfig.interval,
            sma_period: nextConfig.smaPeriod,
            exchange1_fee: nextConfig.exchange1Fee,
            exchange2_fee: nextConfig.exchange2Fee,
            min_profit_percentage: nextConfig.minProfitPercentage,
            spread_side: nextConfig.direction,
            entry_threshold: cycleConfig.entryThreshold,
            exit_threshold: cycleConfig.exitThreshold,
            confirm_entry_points: cycleConfig.confirmEntryPoints,
            confirm_exit_points: cycleConfig.confirmExitPoints,
            include_open_cycle: true,
          }).catch((err: unknown) => {
            console.warn('failed to load spread deviation cycles', err);
            return null;
          })
        : Promise.resolve(null);

      const [detailedSpread, tradeSpread, candle1, candle2, funding1, funding2, deviationCyclesResult] =
        await Promise.all([
        detailedSpreadPromise,
        tradeSpreadPromise,
        candle1Promise,
        candle2Promise,
        funding1Promise,
        funding2Promise,
        deviationCyclesPromise,
      ]);

      let metricHistory: MetricHistoryData = {
        mappedBiasExchange1: [],
        mappedBiasExchange2: [],
        mappedMetricSeries: emptyMetricSeriesData(),
      };
      if (
        fetchSpread &&
        spreadFromIso &&
        !isSpreadPriceMode &&
        indexExchange1 &&
        indexExchange2 &&
        detailedSpread.data.length > 0
      ) {
        const startTs = new Date(spreadFromIso).getTime();
        const endTs = new Date(options.toIso).getTime();
        const indexInterval = toIndexInterval(nextConfig.interval);
        const [index1Result, index2Result] = await Promise.allSettled([
          getIndexPriceHistory({
            exchange: indexExchange1,
            symbol: nextSymbol,
            start_ts: startTs,
            end_ts: endTs,
            interval: indexInterval,
          }),
          getIndexPriceHistory({
            exchange: indexExchange2,
            symbol: nextSymbol,
            start_ts: startTs,
            end_ts: endTs,
            interval: indexInterval,
          }),
        ]);
        if (index1Result.status === 'fulfilled' && index2Result.status === 'fulfilled') {
          metricHistory = buildMetricHistory(
            detailedSpread.data,
            indexCandlesToMap(index1Result.value.data, nextConfig.interval),
            indexCandlesToMap(index2Result.value.data, nextConfig.interval),
            nextConfig.interval,
          );
        }
      }

      const scaleSpreadToPercent = !isSpreadPriceMode;
      const mappedSpreadSeries: SpreadSeriesData = fetchSpread
        ? useTradePriceData
          ? mapTradePriceSpreadSeries(tradeSpread.data, nextConfig.interval)
          : {
            long1Short2Open: mapDetailedSpreadLine(detailedSpread.data, 'long1_short2_open_spread', nextConfig.interval, scaleSpreadToPercent),
            long1Short2Close: mapDetailedSpreadLine(detailedSpread.data, 'long1_short2_close_spread', nextConfig.interval, scaleSpreadToPercent),
            short1Long2Open: mapDetailedSpreadLine(detailedSpread.data, 'short1_long2_open_spread', nextConfig.interval, scaleSpreadToPercent),
            short1Long2Close: mapDetailedSpreadLine(detailedSpread.data, 'short1_long2_close_spread', nextConfig.interval, scaleSpreadToPercent),
            long1Short2OpenLine: mapDetailedSpreadLine(detailedSpread.data, 'long1_short2_open_line', nextConfig.interval, scaleSpreadToPercent),
            long1Short2CloseLine: mapDetailedSpreadLine(detailedSpread.data, 'long1_short2_close_line', nextConfig.interval, scaleSpreadToPercent),
            short1Long2OpenLine: mapDetailedSpreadLine(detailedSpread.data, 'short1_long2_open_line', nextConfig.interval, scaleSpreadToPercent),
            short1Long2CloseLine: mapDetailedSpreadLine(detailedSpread.data, 'short1_long2_close_line', nextConfig.interval, scaleSpreadToPercent),
            long1Short2MidSpread: mapDetailedSpreadLine(detailedSpread.data, 'long1_short2_mid_spread', nextConfig.interval, scaleSpreadToPercent),
            short1Long2MidSpread: mapDetailedSpreadLine(detailedSpread.data, 'short1_long2_mid_spread', nextConfig.interval, scaleSpreadToPercent),
            long1Short2MedianMidSpread: mapDetailedSpreadLine(detailedSpread.data, 'median_long1_short2_mid_spread', nextConfig.interval, scaleSpreadToPercent),
            short1Long2MedianMidSpread: mapDetailedSpreadLine(detailedSpread.data, 'median_short1_long2_mid_spread', nextConfig.interval, scaleSpreadToPercent),
          }
        : emptySpreadSeriesData();
      const mappedCandle1 = fetchCandles ? mapApiCandles(candle1.data, nextConfig.interval) : [];
      const mappedCandle2 = fetchCandles ? mapApiCandles(candle2.data, nextConfig.interval) : [];
      const mappedFunding1 = fetchFunding ? mapOppFundingLine(funding1, nextConfig.interval) : [];
      const mappedFunding2 = fetchFunding ? mapOppFundingLine(funding2, nextConfig.interval) : [];
      const mappedFunding1Settlements = fetchFunding
        ? mapOppFundingSettlements(funding1, nextConfig.interval, mappedFunding1)
        : [];
      const mappedFunding2Settlements = fetchFunding
        ? mapOppFundingSettlements(funding2, nextConfig.interval, mappedFunding2)
        : [];

      return {
        mappedSpreadSeries,
        mappedCandle1,
        mappedCandle2,
        mappedFunding1,
        mappedFunding2,
        mappedFunding1Settlements,
        mappedFunding2Settlements,
        fundingSummary: buildFundingSummaryFromResponses(funding1, funding2),
        deviationCycles: deviationCyclesResult,
        ...metricHistory,
      };
    },
    [historySource, isSpreadPriceMode, oppPriceDataSource],
  );

  const backfillGap = useCallback(
    async (nextConfig: RealtimeSpreadConfig) => {
      const interval = nextConfig.interval;
      const nowIso = new Date().toISOString();
      const nowMs = new Date(nowIso).getTime();
      const spreadLast = lastSpreadTsRef.current;
      const candleLast = lastCandleTsRef.current;
      if (!spreadLast && !candleLast) return;

      const spreadFromIso =
        spreadLast != null ? new Date((spreadLast + interval) * 1000).toISOString() : undefined;
      const candleFromIso =
        candleLast != null ? new Date((candleLast + interval) * 1000).toISOString() : undefined;
      const needSpread = spreadFromIso != null && new Date(spreadFromIso).getTime() < nowMs;
      const needCandles = candleFromIso != null && new Date(candleFromIso).getTime() < nowMs;
      const fundingFromIso = spreadFromIso ?? candleFromIso;
      const needFunding =
        isSpreadPriceMode &&
        fundingFromIso != null &&
        new Date(fundingFromIso).getTime() < nowMs &&
        (needSpread || needCandles);
      if (!needSpread && !needCandles && !needFunding) return;

      const result = await applyHistoryRange(nextConfig, {
        toIso: nowIso,
        spreadFromIso: needSpread ? spreadFromIso : undefined,
        candleFromIso: needCandles ? candleFromIso : undefined,
        fundingFromIso: needFunding ? fundingFromIso : undefined,
        fetchSpread: needSpread,
        fetchCandles: needCandles,
        fetchFunding: needFunding,
      });

      if (needSpread) {
        setSpreadSeriesData((prev) => ({
          long1Short2Open: result.mappedSpreadSeries.long1Short2Open.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2Open),
          long1Short2Close: result.mappedSpreadSeries.long1Short2Close.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2Close),
          short1Long2Open: result.mappedSpreadSeries.short1Long2Open.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2Open),
          short1Long2Close: result.mappedSpreadSeries.short1Long2Close.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2Close),
          long1Short2OpenLine: result.mappedSpreadSeries.long1Short2OpenLine.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2OpenLine),
          long1Short2CloseLine: result.mappedSpreadSeries.long1Short2CloseLine.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2CloseLine),
          short1Long2OpenLine: result.mappedSpreadSeries.short1Long2OpenLine.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2OpenLine),
          short1Long2CloseLine: result.mappedSpreadSeries.short1Long2CloseLine.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2CloseLine),
          long1Short2MidSpread: result.mappedSpreadSeries.long1Short2MidSpread.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2MidSpread),
          short1Long2MidSpread: result.mappedSpreadSeries.short1Long2MidSpread.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2MidSpread),
          long1Short2MedianMidSpread: result.mappedSpreadSeries.long1Short2MedianMidSpread.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2MedianMidSpread),
          short1Long2MedianMidSpread: result.mappedSpreadSeries.short1Long2MedianMidSpread.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2MedianMidSpread),
        }));
        if (!isSpreadPriceMode) {
          setBiasExchange1Data((prev) => result.mappedBiasExchange1.reduce((acc, point) => upsertLine(acc, point), prev));
          setBiasExchange2Data((prev) => result.mappedBiasExchange2.reduce((acc, point) => upsertLine(acc, point), prev));
          setMetricSeriesData((prev) => ({
            long1Short2IndexSpread: result.mappedMetricSeries.long1Short2IndexSpread.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2IndexSpread),
            short1Long2IndexSpread: result.mappedMetricSeries.short1Long2IndexSpread.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2IndexSpread),
            long1Short2AdjustedSpreadMa1m: result.mappedMetricSeries.long1Short2AdjustedSpreadMa1m.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa1m),
            long1Short2AdjustedSpreadMa5m: result.mappedMetricSeries.long1Short2AdjustedSpreadMa5m.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa5m),
            long1Short2AdjustedSpreadMa10m: result.mappedMetricSeries.long1Short2AdjustedSpreadMa10m.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa10m),
            long1Short2AdjustedSpreadMa60m: result.mappedMetricSeries.long1Short2AdjustedSpreadMa60m.reduce((acc, point) => upsertLine(acc, point), prev.long1Short2AdjustedSpreadMa60m),
            short1Long2AdjustedSpreadMa1m: result.mappedMetricSeries.short1Long2AdjustedSpreadMa1m.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa1m),
            short1Long2AdjustedSpreadMa5m: result.mappedMetricSeries.short1Long2AdjustedSpreadMa5m.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa5m),
            short1Long2AdjustedSpreadMa10m: result.mappedMetricSeries.short1Long2AdjustedSpreadMa10m.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa10m),
            short1Long2AdjustedSpreadMa60m: result.mappedMetricSeries.short1Long2AdjustedSpreadMa60m.reduce((acc, point) => upsertLine(acc, point), prev.short1Long2AdjustedSpreadMa60m),
          }));
        }
        const spreadEnd = maxSeriesEndTs([
          result.mappedSpreadSeries.long1Short2Open,
          result.mappedSpreadSeries.long1Short2Close,
          ...(isSpreadPriceMode ? [] : [result.mappedBiasExchange1, result.mappedMetricSeries.long1Short2IndexSpread]),
        ]);
        if (spreadEnd > 0) {
          lastSpreadTsRef.current = Math.max(lastSpreadTsRef.current ?? 0, spreadEnd);
        }
      }

      if (needCandles) {
        setExchange1KlineData((prev) => result.mappedCandle1.reduce((acc, point) => upsertCandle(acc, point), prev));
        setExchange2KlineData((prev) => result.mappedCandle2.reduce((acc, point) => upsertCandle(acc, point), prev));
        const candleEnd = maxSeriesEndTs([result.mappedCandle1, result.mappedCandle2]);
        if (candleEnd > 0) {
          lastCandleTsRef.current = Math.max(lastCandleTsRef.current ?? 0, candleEnd);
        }
      }

      if (needFunding) {
        setExchange1FundingData((prev) => result.mappedFunding1.reduce((acc, point) => upsertLine(acc, point), prev));
        setExchange2FundingData((prev) => result.mappedFunding2.reduce((acc, point) => upsertLine(acc, point), prev));
        setExchange1FundingSettlements((prev) =>
          result.mappedFunding1Settlements.reduce((acc, point) => upsertLine(acc, point), prev),
        );
        setExchange2FundingSettlements((prev) =>
          result.mappedFunding2Settlements.reduce((acc, point) => upsertLine(acc, point), prev),
        );
        setFundingSummary(result.fundingSummary);
        setFundingSummaryUpdatedAtMs(Date.now());
        setFundingRefreshError(null);
      }
      if (needSpread && result.deviationCycles) {
        setDeviationCycles(result.deviationCycles);
      }
    },
    [applyHistoryRange, isSpreadPriceMode],
  );

  const refreshFundingInfo = useCallback(async () => {
    if (!isSpreadPriceMode || !isOppDetectorSource) return;
    const currentConfig = configRef.current;
    const nextInst1 = currentConfig.instId1.trim();
    const nextInst2 = currentConfig.instId2.trim();
    if (!nextInst1 || !nextInst2) {
      setFundingRefreshError('请先输入有效 Inst ID');
      return;
    }

    setFundingRefreshLoading(true);
    setFundingRefreshError(null);
    try {
      const endMs = Date.now();
      const startMs =
        historyQueryStartMsRef.current ??
        new Date(inputUtcToIso(startTimeRef.current)).getTime();
      const [funding1, funding2] = await Promise.all([
        getOppFundingHistory({
          exchange: currentConfig.exchange1,
          inst_id: nextInst1,
          start_ms: startMs,
          end_ms: endMs,
          interval: currentConfig.interval,
        }),
        getOppFundingHistory({
          exchange: currentConfig.exchange2,
          inst_id: nextInst2,
          start_ms: startMs,
          end_ms: endMs,
          interval: currentConfig.interval,
        }),
      ]);
      const nowMs = Date.now();
      setFundingSummary(buildFundingSummaryFromResponses(funding1, funding2));
      setFundingSummaryUpdatedAtMs(nowMs);
      setFundingNowMs(nowMs);
    } catch (err) {
      setFundingRefreshError(err instanceof Error ? err.message : String(err));
    } finally {
      setFundingRefreshLoading(false);
    }
  }, [isOppDetectorSource, isSpreadPriceMode]);

  const openRealtimeStream = useCallback(
    async (nextConfig: RealtimeSpreadConfig, sessionId: number) => {
      resetConnection();
      setStreamStatus((prev) => (prev === 'history_loading' ? 'connecting' : 'reconnecting'));

      const nextInst1 = nextConfig.instId1.trim();
      const nextInst2 = nextConfig.instId2.trim();
      let ticket: string;
      try {
        const ticketResponse = await createOppDetectorWsTicket();
        if (sessionIdRef.current !== sessionId) return;
        ticket = ticketResponse.ticket;
      } catch (err) {
        if (sessionIdRef.current !== sessionId) return;
        setStreamStatus('stale');
        const nextDelay = backoffMsRef.current;
        backoffMsRef.current = Math.min(backoffMsRef.current * 2, 30_000);
        reconnectTimerRef.current = window.setTimeout(() => {
          void openRealtimeStream(nextConfig, sessionId);
        }, nextDelay);
        setError(err instanceof Error ? err.message : String(err));
        return;
      }
      const ws = new WebSocket(
        getRealtimeSpreadWsUrl({
          exchange1: nextConfig.exchange1,
          inst1: nextInst1,
          exchange2: nextConfig.exchange2,
          inst2: nextInst2,
          interval: nextConfig.interval,
          direction: nextConfig.direction,
          ticket,
        }),
      );
      wsRef.current = ws;

      ws.onopen = () => {
        if (sessionIdRef.current !== sessionId) return;
        setStreamStatus('live');
        backoffMsRef.current = 1000;
        void backfillGap(nextConfig).catch(() => {
          setStreamStatus('stale');
        });
      };

      ws.onmessage = (event) => {
        if (sessionIdRef.current !== sessionId) return;
        let message: RealtimeSpreadWsMessage;
        try {
          message = JSON.parse(event.data as string) as RealtimeSpreadWsMessage;
        } catch {
          return;
        }
        if (message.type === 'heartbeat') return;
        if (message.type === 'tick') {
          if (oppPriceDataSourceRef.current === 'trade') return;
          const bucketTs = toIntervalBucketTs(message.ts, nextConfig.interval);
          const scaleSpread = isSpreadPriceMode ? 1 : 100;
          pendingRef.current.long1Short2OpenSpread.push({
            time: bucketTs,
            value: message.long1_short2_open_spread * scaleSpread,
          });
          pendingRef.current.long1Short2CloseSpread.push({
            time: bucketTs,
            value: message.long1_short2_close_spread * scaleSpread,
          });
          pendingRef.current.short1Long2OpenSpread.push({
            time: bucketTs,
            value: message.short1_long2_open_spread * scaleSpread,
          });
          pendingRef.current.short1Long2CloseSpread.push({
            time: bucketTs,
            value: message.short1_long2_close_spread * scaleSpread,
          });
          lastSpreadTsRef.current = Math.max(lastSpreadTsRef.current ?? 0, bucketTs);
          return;
        }
        if (message.type === 'index_metrics') {
          if (isSpreadPriceMode) return;
          const metricsMessage = message as RealtimeIndexMetricsMessage;
          const bucketTs = toIntervalBucketTs(metricsMessage.ts, nextConfig.interval);
          pendingRef.current.biasExchange1.push({
            time: bucketTs,
            value: metricsMessage.bias_exchange1 * 100,
          });
          pendingRef.current.biasExchange2.push({
            time: bucketTs,
            value: metricsMessage.bias_exchange2 * 100,
          });
          pendingRef.current.long1Short2IndexSpread.push({
            time: bucketTs,
            value: metricsMessage.long1_short2_index_spread * 100,
          });
          pendingRef.current.short1Long2IndexSpread.push({
            time: bucketTs,
            value: metricsMessage.short1_long2_index_spread * 100,
          });
          pendingRef.current.long1Short2AdjustedSpreadMa1m.push({
            time: bucketTs,
            value: metricsMessage.long1_short2_adjusted_spread_ma_1m * 100,
          });
          pendingRef.current.long1Short2AdjustedSpreadMa5m.push({
            time: bucketTs,
            value: metricsMessage.long1_short2_adjusted_spread_ma_5m * 100,
          });
          pendingRef.current.long1Short2AdjustedSpreadMa10m.push({
            time: bucketTs,
            value: metricsMessage.long1_short2_adjusted_spread_ma_10m * 100,
          });
          pendingRef.current.long1Short2AdjustedSpreadMa60m.push({
            time: bucketTs,
            value: metricsMessage.long1_short2_adjusted_spread_ma_60m * 100,
          });
          pendingRef.current.short1Long2AdjustedSpreadMa1m.push({
            time: bucketTs,
            value: metricsMessage.short1_long2_adjusted_spread_ma_1m * 100,
          });
          pendingRef.current.short1Long2AdjustedSpreadMa5m.push({
            time: bucketTs,
            value: metricsMessage.short1_long2_adjusted_spread_ma_5m * 100,
          });
          pendingRef.current.short1Long2AdjustedSpreadMa10m.push({
            time: bucketTs,
            value: metricsMessage.short1_long2_adjusted_spread_ma_10m * 100,
          });
          pendingRef.current.short1Long2AdjustedSpreadMa60m.push({
            time: bucketTs,
            value: metricsMessage.short1_long2_adjusted_spread_ma_60m * 100,
          });
          lastSpreadTsRef.current = Math.max(lastSpreadTsRef.current ?? 0, bucketTs);
          return;
        }
        if (message.type === 'kline_update') {
          if (oppPriceDataSourceRef.current === 'trade') return;
          const klineMessage = message as RealtimeKlineUpdateMessage;
          const bucketTs = toIntervalBucketTs(klineMessage.ts, nextConfig.interval);
          const candle: CandlePoint = {
            time: bucketTs,
            open: klineMessage.open,
            high: klineMessage.high,
            low: klineMessage.low,
            close: klineMessage.close,
          };
          if (klineMessage.source === 'exchange1') {
            pendingRef.current.exchange1Kline.push(candle);
          } else {
            pendingRef.current.exchange2Kline.push(candle);
          }
          lastCandleTsRef.current = Math.max(lastCandleTsRef.current ?? 0, bucketTs);
        }
      };

      ws.onerror = () => {
        if (sessionIdRef.current !== sessionId) return;
        setStreamStatus('stale');
      };

      ws.onclose = () => {
        if (sessionIdRef.current !== sessionId) return;
        setStreamStatus('reconnecting');
        const nextDelay = backoffMsRef.current;
        backoffMsRef.current = Math.min(backoffMsRef.current * 2, 30_000);
        reconnectTimerRef.current = window.setTimeout(() => {
          void openRealtimeStream(nextConfig, sessionId);
        }, nextDelay);
      };
    },
    [backfillGap, isSpreadPriceMode, resetConnection],
  );

  const clearDisplayedChartData = useCallback(() => {
    setSpreadSeriesData(emptySpreadSeriesData());
    setExchange1KlineData([]);
    setExchange2KlineData([]);
    setExchange1FundingData([]);
    setExchange2FundingData([]);
    setExchange1FundingSettlements([]);
    setExchange2FundingSettlements([]);
    setFundingSummary({
      exchange1CurrentRate: null,
      exchange2CurrentRate: null,
      exchange1NextFundingTimeMs: null,
      exchange2NextFundingTimeMs: null,
    });
    setFundingSummaryUpdatedAtMs(null);
    setFundingRefreshError(null);
    setBiasExchange1Data([]);
    setBiasExchange2Data([]);
    setMetricSeriesData(emptyMetricSeriesData());
    setDeviationCycles(null);
  }, []);

  const runStartRealtime = useCallback(async (
    overrideConfig?: RealtimeSpreadConfig,
    options?: { fromPositionLink?: boolean },
  ) => {
      setError(null);

      if (!options?.fromPositionLink) {
        explicitPositionSelectionRef.current = false;
        setSelectedPositionGroupId(null);
        setChartLinkedPositionGroup(null);
      }

      const nextConfig = overrideConfig ?? config;
      const nextSymbol = normalizeSymbolInput(nextConfig.symbol);
      const nextInst1 = nextConfig.instId1.trim();
      const nextInst2 = nextConfig.instId2.trim();
      if (!nextSymbol || !nextInst1 || !nextInst2) {
        setError('请先输入有效 Symbol 并确认 Inst ID');
        return;
      }
      const startIso = inputUtcToIso(startTimeRef.current);
      const endIso = new Date().toISOString();
      const startMs = new Date(startIso).getTime();
      if (startMs >= Date.now()) {
        setError('开始时间必须早于当前时间');
        return;
      }
      const preset = getIntervalPreset(nextConfig.interval);
      const lookbackMs = Date.now() - startMs;
      if (lookbackMs > preset.maxMs) {
        setError(`该 Interval 最多回看 ${formatLookbackDuration(preset.maxMs)}，请调整开始时间`);
        return;
      }

      sessionIdRef.current += 1;
      const sessionId = sessionIdRef.current;
      resetConnection();
      clearDisplayedChartData();
      setStreamStatus('history_loading');
      lastSpreadTsRef.current = null;
      lastCandleTsRef.current = null;
      pendingRef.current = emptyPendingUpdates();
      historyQueryStartMsRef.current = startMs;

      try {
        const result = await applyHistoryRange(nextConfig, {
          spreadFromIso: startIso,
          candleFromIso: startIso,
          fundingFromIso: startIso,
          toIso: endIso,
        });
        if (sessionIdRef.current !== sessionId) return;
        setSpreadSeriesData(result.mappedSpreadSeries);
        setDeviationCycles(result.deviationCycles);
        if (isSpreadPriceMode) {
          setExchange1KlineData(result.mappedCandle1);
          setExchange2KlineData(result.mappedCandle2);
          setExchange1FundingData(result.mappedFunding1);
          setExchange2FundingData(result.mappedFunding2);
          setExchange1FundingSettlements(result.mappedFunding1Settlements);
          setExchange2FundingSettlements(result.mappedFunding2Settlements);
          setFundingSummary(result.fundingSummary);
          setFundingSummaryUpdatedAtMs(Date.now());
          setFundingRefreshError(null);
          setBiasExchange1Data([]);
          setBiasExchange2Data([]);
          setMetricSeriesData(emptyMetricSeriesData());
        } else {
          setExchange1FundingData([]);
          setExchange2FundingData([]);
          setExchange1FundingSettlements([]);
          setExchange2FundingSettlements([]);
          setFundingSummary({
            exchange1CurrentRate: null,
            exchange2CurrentRate: null,
            exchange1NextFundingTimeMs: null,
            exchange2NextFundingTimeMs: null,
          });
          setFundingSummaryUpdatedAtMs(null);
          setFundingRefreshError(null);
          setBiasExchange1Data(result.mappedBiasExchange1);
          setBiasExchange2Data(result.mappedBiasExchange2);
          setMetricSeriesData(result.mappedMetricSeries);
          setExchange1KlineData(result.mappedCandle1);
          setExchange2KlineData(result.mappedCandle2);
        }
        const lastSpreadTs = maxSeriesEndTs([
          result.mappedSpreadSeries.long1Short2Open,
          result.mappedSpreadSeries.long1Short2Close,
          result.mappedSpreadSeries.short1Long2Open,
          result.mappedSpreadSeries.short1Long2Close,
          ...(isSpreadPriceMode ? [] : [
            result.mappedBiasExchange1,
            result.mappedBiasExchange2,
            result.mappedMetricSeries.long1Short2IndexSpread,
            result.mappedMetricSeries.long1Short2AdjustedSpreadMa1m,
          ]),
        ]);
        const lastCandleTs = maxSeriesEndTs([result.mappedCandle1, result.mappedCandle2]);
        if (lastSpreadTs > 0) {
          lastSpreadTsRef.current = lastSpreadTs;
        }
        if (lastCandleTs > 0) {
          lastCandleTsRef.current = lastCandleTs;
        }
        if (!options?.fromPositionLink) {
          syncChartLinkedPositionGroup(positionGroupsRef.current, nextConfig);
        }
        await openRealtimeStream(nextConfig, sessionId);
      } catch (err) {
        setStreamStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [applyHistoryRange, clearDisplayedChartData, config, isSpreadPriceMode, openRealtimeStream, resetConnection, syncChartLinkedPositionGroup],
  );

  useEffect(() => {
    if (!isSpreadPriceMode || !isOppDetectorSource) return;
    if (didAutoLoadLargestPositionRef.current) return;

    if (hasChartSearchParams(searchParams) || configTouchedByUserRef.current || explicitPositionSelectionRef.current) {
      didAutoLoadLargestPositionRef.current = true;
      return;
    }

    if (streamStatus !== 'idle') {
      didAutoLoadLargestPositionRef.current = true;
      return;
    }

    const largestGroup = pickLargestPositionGroup(positionGroupsRef.current);
    if (!largestGroup) return;

    const chartConfig = chartConfigFromPositionGroup(largestGroup);
    if (!chartConfig) return;

    didAutoLoadLargestPositionRef.current = true;
    explicitPositionSelectionRef.current = true;
    setSelectedPositionGroupId(largestGroup.group_id);
    setChartLinkedPositionGroup(largestGroup);

    const nextConfig: RealtimeSpreadConfig = {
      ...configRef.current,
      symbol: chartConfig.symbol,
      exchange1: chartConfig.exchange1,
      exchange2: chartConfig.exchange2,
      instId1: chartConfig.instId1,
      instId2: chartConfig.instId2,
      direction: chartConfig.direction,
    };
    configRef.current = nextConfig;
    setConfig(nextConfig);
    void runStartRealtime(nextConfig, { fromPositionLink: true });
  }, [
    isOppDetectorSource,
    isSpreadPriceMode,
    positionGroupsRevision,
    runStartRealtime,
    searchParams,
    streamStatus,
  ]);

  useEffect(() => {
    if (!startTimeUserChangeRef.current) return;
    startTimeUserChangeRef.current = false;

    const shouldReload =
      streamStatus === 'live' ||
      streamStatus === 'connecting' ||
      streamStatus === 'reconnecting' ||
      streamStatus === 'stale' ||
      streamStatus === 'history_loading';
    if (shouldReload) {
      void runStartRealtime();
    }
  }, [startTime, runStartRealtime, streamStatus]);

  const selectInterval = useCallback(
    (seconds: number) => {
      if (configRef.current.interval === seconds) return;
      const nextStart = defaultStartForInterval(seconds);
      const nextConfig = { ...configRef.current, interval: seconds };
      configRef.current = nextConfig;
      startTimeRef.current = nextStart;
      setConfig(nextConfig);
      setStartTime(nextStart);

      const shouldReload =
        streamStatus === 'live' ||
        streamStatus === 'connecting' ||
        streamStatus === 'reconnecting' ||
        streamStatus === 'stale' ||
        streamStatus === 'history_loading';
      if (shouldReload) {
        void runStartRealtime(nextConfig);
      }
    },
    [runStartRealtime, streamStatus],
  );

  const applyPositionGroupChart = useCallback(
    (group: PositionGroupView) => {
      const chartConfig = chartConfigFromPositionGroup(group);
      if (!chartConfig) return;
      explicitPositionSelectionRef.current = true;
      setSelectedPositionGroupId(group.group_id);
      setChartLinkedPositionGroup(group);
      const nextConfig: RealtimeSpreadConfig = {
        ...configRef.current,
        symbol: chartConfig.symbol,
        exchange1: chartConfig.exchange1,
        exchange2: chartConfig.exchange2,
        instId1: chartConfig.instId1,
        instId2: chartConfig.instId2,
        direction: chartConfig.direction,
      };
      setConfig(nextConfig);
      chartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      void runStartRealtime(nextConfig, { fromPositionLink: true });
    },
    [runStartRealtime],
  );

  const handleChartLinkedGroupUpdate = useCallback((group: PositionGroupView | null) => {
    if (group) {
      explicitPositionSelectionRef.current = true;
      setChartLinkedPositionGroup(group);
    }
  }, []);

  const onPairSelectorChange = useCallback((next: SpreadPairSelectorValue) => {
    configTouchedByUserRef.current = true;
    setConfig((prev) => ({
      ...prev,
      symbol: next.symbol,
      exchange1: next.exchange1,
      exchange2: next.exchange2,
      instId1: next.instId1,
      instId2: next.instId2,
      ...(next.direction ? { direction: next.direction } : {}),
    }));
  }, []);

  const toggleOppPriceDataSource = useCallback(() => {
    const nextSource: OppSpreadPriceDataSource = oppPriceDataSourceRef.current === 'l1' ? 'trade' : 'l1';
    saveOppPriceDataSource(nextSource);
    setOppPriceDataSource(nextSource);
    if (!isOppDetectorSource) return;
    clearDisplayedChartData();
    setDeviationCycles(null);
    setStreamStatus('history_loading');
    void runStartRealtime(configRef.current);
  }, [clearDisplayedChartData, isOppDetectorSource, runStartRealtime]);

  const effectiveSpreadViewMode: DetailedSpreadViewMode = isTradePriceDataSource ? 'mid' : spreadViewMode;

  const isChartLoading = streamStatus === 'history_loading' || streamStatus === 'connecting';

  useEffect(() => {
    if (!pendingAutoStart) return;
    setPendingAutoStart(false);
    void runStartRealtime();
  }, [pendingAutoStart, runStartRealtime]);

  const refreshOppDetailedSpread = useCallback(async () => {
    if (!isOppDetectorSource) return;
    const currentConfig = configRef.current;
    const useTradePriceData = oppPriceDataSourceRef.current === 'trade';
    const startIso = inputUtcToIso(startTimeRef.current);
    const endIso = new Date().toISOString();
    if (new Date(startIso).getTime() >= Date.now()) return;

    try {
      const result = await applyHistoryRange(currentConfig, {
        spreadFromIso: startIso,
        candleFromIso: useTradePriceData ? startIso : undefined,
        toIso: endIso,
        fetchSpread: true,
        fetchCandles: useTradePriceData,
      });
      setSpreadSeriesData(result.mappedSpreadSeries);
      if (useTradePriceData) {
        setExchange1KlineData(result.mappedCandle1);
        setExchange2KlineData(result.mappedCandle2);
        const candleEnd = maxSeriesEndTs([result.mappedCandle1, result.mappedCandle2]);
        if (candleEnd > 0) {
          lastCandleTsRef.current = candleEnd;
        }
      } else {
        setDeviationCycles(result.deviationCycles);
      }
      const spreadEnd = maxSeriesEndTs([
        result.mappedSpreadSeries.long1Short2Open,
        result.mappedSpreadSeries.long1Short2Close,
        result.mappedSpreadSeries.short1Long2Open,
        result.mappedSpreadSeries.short1Long2Close,
        result.mappedSpreadSeries.long1Short2MidSpread,
        result.mappedSpreadSeries.short1Long2MidSpread,
      ]);
      if (spreadEnd > 0) {
        lastSpreadTsRef.current = spreadEnd;
      }
      if (isSpreadPriceMode) {
        setFundingSummary(result.fundingSummary);
        setFundingSummaryUpdatedAtMs(Date.now());
        setFundingNowMs(Date.now());
        setFundingRefreshError(null);
        setExchange1FundingData(result.mappedFunding1);
        setExchange2FundingData(result.mappedFunding2);
        setExchange1FundingSettlements(result.mappedFunding1Settlements);
        setExchange2FundingSettlements(result.mappedFunding2Settlements);
      }
    } catch {
      // keep WS stream running; next interval will retry
    }
  }, [applyHistoryRange, isOppDetectorSource, isSpreadPriceMode]);

  useEffect(() => {
    if (!isOppDetectorSource) return;
    const isStreaming =
      streamStatus === 'live' ||
      streamStatus === 'connecting' ||
      streamStatus === 'reconnecting' ||
      streamStatus === 'stale';
    if (!isStreaming) return;

    const refreshMs = Math.max(1000, config.interval * 1000);
    const timer = window.setInterval(() => {
      void refreshOppDetailedSpread();
    }, refreshMs);
    return () => window.clearInterval(timer);
  }, [config.interval, isOppDetectorSource, refreshOppDetailedSpread, streamStatus]);

  useEffect(() => {
    if (!spreadDetailSettingsOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const root = spreadDetailSettingsRef.current;
      if (root && !root.contains(event.target as Node)) {
        setSpreadDetailSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [spreadDetailSettingsOpen]);

  useEffect(() => {
    if (!deviationCycleSettingsOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const root = deviationCycleSettingsRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setDeviationCycleSettingsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [deviationCycleSettingsOpen]);

  const refreshDeviationCycles = useCallback(async () => {
    if (!isOppDetectorSource || !isSpreadPriceMode) return;
    const currentConfig = configRef.current;
    const cycleConfig = deviationCycleConfigRef.current;
    const startMs = historyQueryStartMsRef.current ?? new Date(inputUtcToIso(startTimeRef.current)).getTime();
    const endMs = Date.now();
    const nextInst1 = currentConfig.instId1.trim();
    const nextInst2 = currentConfig.instId2.trim();
    if (!nextInst1 || !nextInst2) return;

    try {
      const result = await getOppSpreadDeviationCycles({
        exchange1: currentConfig.exchange1,
        inst_id1: nextInst1,
        exchange2: currentConfig.exchange2,
        inst_id2: nextInst2,
        start_ms: startMs,
        end_ms: endMs,
        interval: currentConfig.interval,
        sma_period: currentConfig.smaPeriod,
        exchange1_fee: currentConfig.exchange1Fee,
        exchange2_fee: currentConfig.exchange2Fee,
        min_profit_percentage: currentConfig.minProfitPercentage,
        spread_side: currentConfig.direction,
        entry_threshold: cycleConfig.entryThreshold,
        exit_threshold: cycleConfig.exitThreshold,
        confirm_entry_points: cycleConfig.confirmEntryPoints,
        confirm_exit_points: cycleConfig.confirmExitPoints,
        include_open_cycle: true,
      });
      setDeviationCycles(result);
      setDeviationCycleConfigError(null);
    } catch (err) {
      setDeviationCycleConfigError(err instanceof Error ? err.message : '波动周期重算失败');
    }
  }, [isOppDetectorSource, isSpreadPriceMode]);

  const toggleSpreadDirection = useCallback(() => {
    const nextDirection: RealtimeSpreadDirection =
      configRef.current.direction === 'long1_short2' ? 'short1_long2' : 'long1_short2';
    configRef.current = { ...configRef.current, direction: nextDirection };
    setConfig((prev) => ({ ...prev, direction: nextDirection }));
    if (isOppDetectorSource && isSpreadPriceMode && !isTradePriceDataSource) {
      void refreshDeviationCycles();
    }
  }, [isOppDetectorSource, isSpreadPriceMode, isTradePriceDataSource, refreshDeviationCycles]);

  const toggleKlineDisplayMode = useCallback(() => {
    setKlineDisplayMode((mode) => {
      const next: KlineDisplayMode = mode === 'candle' ? 'close-line' : 'candle';
      saveKlineDisplayMode(next);
      return next;
    });
  }, []);

  const setDeviationCycleField =
    (field: keyof OppDeviationCycleConfig) => (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const parsed = field.endsWith('Points') ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
      setDeviationCycleConfig((prev) => ({
        ...prev,
        [field]: Number.isFinite(parsed) ? parsed : prev[field],
      }));
    };

  const applySpreadDetailSettings = useCallback(() => {
    setSpreadDetailSettingsOpen(false);
    void refreshOppDetailedSpread();
  }, [refreshOppDetailedSpread]);

  const applyDeviationCycleSettings = useCallback(() => {
    const cycleConfig = deviationCycleConfigRef.current;
    if (cycleConfig.entryThreshold <= 0 || !Number.isFinite(cycleConfig.entryThreshold)) {
      setDeviationCycleConfigError('进入阈值必须大于 0');
      return;
    }
    if (cycleConfig.exitThreshold < 0 || !Number.isFinite(cycleConfig.exitThreshold)) {
      setDeviationCycleConfigError('退出阈值不能为负数');
      return;
    }
    if (cycleConfig.exitThreshold >= cycleConfig.entryThreshold) {
      setDeviationCycleConfigError('退出阈值必须小于进入阈值');
      return;
    }
    if (cycleConfig.confirmEntryPoints < 1 || cycleConfig.confirmExitPoints < 1) {
      setDeviationCycleConfigError('确认点数至少为 1');
      return;
    }
    setDeviationCycleConfigError(null);
    setDeviationCycleSettingsOpen(false);
    void refreshDeviationCycles();
  }, [refreshDeviationCycles]);

  const nextNetFunding = useMemo(() => {
    const legs = directionLegFunding(
      config.direction,
      fundingSummary.exchange1CurrentRate,
      fundingSummary.exchange1NextFundingTimeMs,
      fundingSummary.exchange2CurrentRate,
      fundingSummary.exchange2NextFundingTimeMs,
    );
    return computeNextNetFundingRate(legs);
  }, [config.direction, fundingSummary]);

  useEffect(() => {
    if (!isSpreadPriceMode) return;
    const timer = window.setInterval(() => setFundingNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [isSpreadPriceMode]);

  useEffect(() => {
    setChartViewportHeight(
      loadChartViewportHeight(chartHeightStorageKey, defaultChartViewportHeight),
    );
  }, [chartHeightStorageKey, defaultChartViewportHeight]);

  useEffect(() => {
    setPaneLayout(loadChartPaneLayout(paneLayoutStorageKey, isSpreadPriceMode));
  }, [isSpreadPriceMode, paneLayoutStorageKey]);

  const handlePaneLayoutSnapshot = useCallback(
    (next: ChartPaneLayout) => {
      setPaneLayout((prev) => {
        const unchanged = prev.stretch.every(
          (value, index) => Math.abs(value - next.stretch[index]) < 0.001,
        );
        if (unchanged) return prev;
        const merged = { ...prev, stretch: next.stretch };
        saveChartPaneLayout(paneLayoutStorageKey, merged);
        return merged;
      });
    },
    [paneLayoutStorageKey],
  );

  const togglePaneVisible = useCallback(
    (index: number) => {
      setPaneLayout((prev) => {
        const isVisible = prev.visible[index] ?? true;
        if (isVisible) {
          const visibleCount = prev.visible.filter(Boolean).length;
          if (visibleCount <= 1) {
            return prev;
          }
        }
        const visible = prev.visible.map((value, i) => (i === index ? !isVisible : value));
        const next = { ...prev, visible };
        saveChartPaneLayout(paneLayoutStorageKey, next);
        return next;
      });
    },
    [paneLayoutStorageKey],
  );

  const renderPaneVisibilityOptions = () => (
    <div className="realtime-spread-pane-visibility-list">
      {paneLabels.map((label, index) => {
        const isVisible = paneLayout.visible[index] ?? true;
        const visibleCount = paneLayout.visible.filter(Boolean).length;
        const isLastVisible = isVisible && visibleCount <= 1;
        return (
          <label key={label} className="realtime-spread-pane-visibility-option">
            <input
              type="checkbox"
              checked={isVisible}
              disabled={isLastVisible}
              onChange={() => togglePaneVisible(index)}
            />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  );

  const renderLegendToggle = (
    key: keyof ChartSeriesVisibility,
    dotClassName: string,
    label: string,
  ) => (
    <button
      type="button"
      className={`realtime-spread-legend-toggle${seriesVisibility[key] ? '' : ' is-hidden'}`}
      onClick={() => toggleSeriesVisibility(key)}
      aria-pressed={seriesVisibility[key]}
    >
      <i className={`legend-dot ${dotClassName}`} />
      {label}
    </button>
  );

  const deviationCycleLegendLabel = useMemo(() => {
    if (!deviationCycles) return '波动周期';
    const { completed_cycle_count, has_open_cycle } = deviationCycles.summary;
    if (completed_cycle_count === 0 && !has_open_cycle) return '波动周期 (0)';
    return `波动周期 (${completed_cycle_count}${has_open_cycle ? '+进行中' : ''})`;
  }, [deviationCycles]);

  const renderDeviationCycleSettingsPanel = () => (
    <>
      <button
        type="button"
        className={`realtime-spread-chart-settings-btn is-cycle${deviationCycleSettingsOpen ? ' is-open' : ''}`}
        aria-expanded={deviationCycleSettingsOpen}
        aria-haspopup="dialog"
        onClick={() => {
          setDeviationCycleSettingsOpen((open) => !open);
          setSpreadDetailSettingsOpen(false);
        }}
      >
        周期阈值
      </button>
      {deviationCycleSettingsOpen ? (
        <div
          className="realtime-spread-chart-settings-panel is-cycle"
          role="dialog"
          aria-label="波动周期检测阈值"
        >
          <div className="realtime-spread-chart-settings-title">波动周期检测阈值</div>
          <label>
            <span>进入阈值 (entry)</span>
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              value={deviationCycleConfig.entryThreshold}
              onChange={setDeviationCycleField('entryThreshold')}
            />
          </label>
          <label>
            <span>退出阈值 (exit)</span>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={deviationCycleConfig.exitThreshold}
              onChange={setDeviationCycleField('exitThreshold')}
            />
          </label>
          <label>
            <span>进入确认点数</span>
            <input
              type="number"
              min="1"
              step="1"
              value={deviationCycleConfig.confirmEntryPoints}
              onChange={setDeviationCycleField('confirmEntryPoints')}
            />
          </label>
          <label>
            <span>退出确认点数</span>
            <input
              type="number"
              min="1"
              step="1"
              value={deviationCycleConfig.confirmExitPoints}
              onChange={setDeviationCycleField('confirmExitPoints')}
            />
          </label>
          <p className="realtime-spread-cycle-settings-hint">
            退出持续约{' '}
            {formatLookbackDuration(deviationCycleConfig.confirmExitPoints * config.interval * 1000)}
            （{deviationCycleConfig.confirmExitPoints} × {toIndexInterval(config.interval)}）
          </p>
          <p className="realtime-spread-cycle-settings-hint">
            基于 {config.direction}_mid_spread 相对 median 偏离；S↑ 开始 · E↓ 结束 · 紫带为周期区间
          </p>
          {deviationCycleConfigError ? (
            <p className="realtime-spread-cycle-settings-error">{deviationCycleConfigError}</p>
          ) : null}
          <button
            type="button"
            className="realtime-spread-chart-settings-apply"
            onClick={applyDeviationCycleSettings}
          >
            应用并重算
          </button>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="realtime-spread-page is-full-bleed">
      <div className="realtime-spread-body">
        <TradeVolumeProvider
          enabled={isOppDetectorSource && !!inst1 && !!inst2}
          exchange1={config.exchange1}
          exchange2={config.exchange2}
          exchange1InstId={inst1}
          exchange2InstId={inst2}
        >
        <div className="realtime-spread-stack" ref={mainColumnRef}>
          {error ? <div className="realtime-spread-error">{error}</div> : null}

          <section className="realtime-spread-filter-shell" aria-label="筛选栏">
            <div className="realtime-spread-filter-shell-main">
              <SpreadPairSelector
                value={{
                  symbol: config.symbol,
                  exchange1: config.exchange1,
                  exchange2: config.exchange2,
                  instId1: config.instId1,
                  instId2: config.instId2,
                  direction: config.direction,
                }}
                exchanges={exchanges}
                useSymbolSearch={isOppDetectorSource}
                showDirection={!isSpreadPriceMode}
                panelLayout="drawer"
                showSpreadTop={isOppDetectorSource}
                category={isSpreadPriceMode ? symbolCategory : null}
                categoryLoaded={isSpreadPriceMode ? symbolCategoryLoaded : undefined}
                onChange={onPairSelectorChange}
                onConfirm={() => {
                  void runStartRealtime();
                }}
              />
            </div>
            <div className="realtime-spread-filter-shell-overview" aria-label="概览信息">
              <TradingOverviewBar
                exchange1={config.exchange1}
                exchange2={config.exchange2}
                exchange1InstId={inst1}
                exchange2InstId={inst2}
                showVolume={isOppDetectorSource && !!inst1 && !!inst2}
                showFunding={isSpreadPriceMode}
                funding={{
                  direction: config.direction,
                  fundingSummary,
                  fundingInstrumentMeta,
                  nextNetFunding,
                  fundingNowMs,
                  fundingSummaryUpdatedAtMs,
                  fundingRefreshLoading,
                  fundingRefreshError,
                  onRefresh: () => void refreshFundingInfo(),
                  refreshDisabled: !isOppDetectorSource || !inst1 || !inst2,
                }}
              />
            </div>
          </section>

          <HorizontalSplitPane
            className="realtime-spread-workspace"
            storageKey="realtime-spread-body-split"
            defaultRatio={2 / 3}
            minLeftPx={480}
            minRightPx={300}
            left={
              <div className="realtime-spread-left-column" ref={mainColumnRef}>
          <section className="realtime-spread-content-shell" ref={chartSectionRef} aria-label="图表">
        <div className="realtime-spread-view-tabs-bar">
          <div className="realtime-spread-view-tabs" role="tablist" aria-label="主视图">
            <button
              type="button"
              role="tab"
              aria-selected={chartSectionTab === 'chart'}
              className={chartSectionTab === 'chart' ? 'is-active' : undefined}
              onClick={() => setChartSectionTab('chart')}
            >
              Chart
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={chartSectionTab === 'datadetail'}
              className={chartSectionTab === 'datadetail' ? 'is-active' : undefined}
              onClick={() => setChartSectionTab('datadetail')}
            >
              Data Detail
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={chartSectionTab === 'basicinfo'}
              className={chartSectionTab === 'basicinfo' ? 'is-active' : undefined}
              onClick={() => setChartSectionTab('basicinfo')}
            >
              Basic Info
            </button>
          </div>
        </div>

      <section className="realtime-spread-card is-chart-resizable is-tab-panel">
        {chartSectionTab === 'chart' ? (
          <>
        <div className="realtime-spread-combined-legend">
          <div className="realtime-spread-chart-settings" ref={spreadDetailSettingsRef}>
            <button
              type="button"
              className={`realtime-spread-chart-settings-btn${spreadDetailSettingsOpen ? ' is-open' : ''}`}
              aria-expanded={spreadDetailSettingsOpen}
              aria-haspopup="dialog"
              onClick={() => {
                setSpreadDetailSettingsOpen((open) => !open);
                setDeviationCycleSettingsOpen(false);
              }}
            >
              设置
            </button>
            {spreadDetailSettingsOpen ? (
              <div className="realtime-spread-chart-settings-panel" role="dialog" aria-label="图表设置">
                <div className="realtime-spread-chart-settings-section is-pane-visibility">
                  <div className="realtime-spread-chart-settings-title">显示子图</div>
                  {renderPaneVisibilityOptions()}
                </div>
                {isOppDetectorSource && !isTradePriceDataSource ? (
                  <div className="realtime-spread-chart-settings-section is-spread-detail">
                    <div className="realtime-spread-chart-settings-title">Spread Detail 计算参数</div>
                    <label>
                      <span>SMA 周期</span>
                      <input type="number" min="1" value={config.smaPeriod} onChange={setField('smaPeriod')} required />
                    </label>
                    <label>
                      <span>交易所 1 Fee</span>
                      <input
                        type="number"
                        min="0"
                        step="0.00001"
                        value={config.exchange1Fee}
                        onChange={setField('exchange1Fee')}
                        required
                      />
                    </label>
                    <label>
                      <span>交易所 2 Fee</span>
                      <input
                        type="number"
                        min="0"
                        step="0.00001"
                        value={config.exchange2Fee}
                        onChange={setField('exchange2Fee')}
                        required
                      />
                    </label>
                    <label>
                      <span>Min Profit %</span>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        value={config.minProfitPercentage}
                        onChange={setField('minProfitPercentage')}
                        required
                      />
                    </label>
                    <button type="button" className="realtime-spread-chart-settings-apply" onClick={applySpreadDetailSettings}>
                      应用并重算
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {isTradePriceDataSource ? (
            renderLegendToggle('midSpread', 'legend-dot-spread', `Trade 价差 (${config.direction})`)
          ) : spreadViewMode === 'orderbook' || !isSpreadPriceMode ? (
            <>
              {renderLegendToggle(
                'openSpread',
                'legend-dot-spread',
                isSpreadPriceMode ? `主图：${config.direction}_open_spread` : `主图：${config.direction}_open_spread %`,
              )}
              {renderLegendToggle(
                'closeSpread',
                'legend-dot-close-spread',
                isSpreadPriceMode ? `主图：${config.direction}_close_spread` : `主图：${config.direction}_close_spread %`,
              )}
            </>
          ) : null}
          {isSpreadPriceMode ? (
            <>
              {!isTradePriceDataSource && spreadViewMode === 'orderbook' ? (
                <>
                  {renderLegendToggle('openLine', 'legend-dot-open-line', `${config.direction}_open_line`)}
                  {renderLegendToggle('closeLine', 'legend-dot-close-line', `${config.direction}_close_line`)}
                </>
              ) : !isTradePriceDataSource ? (
                <>
                  {renderLegendToggle('midSpread', 'legend-dot-spread', `${config.direction}_mid_spread`)}
                  {renderLegendToggle('medianMidSpread', 'legend-dot-close-line', `median_${config.direction}_mid_spread`)}
                </>
              ) : null}
              {renderLegendToggle(
                'klineExchange1',
                'legend-dot-ex1',
                isTradePriceDataSource ? `Trade K 线：${config.exchange1}` : `K 线：${config.exchange1}`,
              )}
              {renderLegendToggle(
                'klineExchange2',
                'legend-dot-ex2',
                isTradePriceDataSource ? `Trade K 线：${config.exchange2}` : `K 线：${config.exchange2}`,
              )}
              {renderLegendToggle('fundingExchange1', 'legend-dot-ex1', `Funding：${config.exchange1}`)}
              {renderLegendToggle('fundingExchange2', 'legend-dot-ex2', `Funding：${config.exchange2}`)}
              {renderLegendToggle(
                'netFunding',
                'legend-dot-net-funding',
                `净资费 (${spreadDirectionShortLabel(config.direction)})`,
              )}
              <span><i className="legend-dot legend-dot-settlement" />Funding 结算点</span>
              {isOppDetectorSource && !isTradePriceDataSource ? (
                <>
                  {renderLegendToggle(
                    'deviationCycles',
                    'legend-dot-deviation-cycle',
                    deviationCycleLegendLabel,
                  )}
                  <span className="realtime-spread-cycle-marker-legend">S↑ E↓</span>
                </>
              ) : null}
            </>
          ) : (
            <>
              {renderLegendToggle('klineExchange1', 'legend-dot-ex1', `子图 K 线：${config.exchange1}`)}
              {renderLegendToggle('klineExchange2', 'legend-dot-ex2', `子图 K 线：${config.exchange2}`)}
              {renderLegendToggle('biasExchange1', 'legend-dot-bias1', `bias：${config.exchange1}`)}
              {renderLegendToggle('biasExchange2', 'legend-dot-bias2', `bias：${config.exchange2}`)}
              {renderLegendToggle('indexSpread', 'legend-dot-index-spread', 'index spread %')}
              {renderLegendToggle('offsetMa1m', 'legend-dot-adjusted-spread', 'offset MA 1m %')}
              {renderLegendToggle('offsetMa5m', 'legend-dot-adjusted-spread-5m', 'offset MA 5m %')}
              {renderLegendToggle('offsetMa10m', 'legend-dot-adjusted-spread-10m', 'offset MA 10m %')}
              {renderLegendToggle('offsetMa60m', 'legend-dot-adjusted-spread-60m', 'offset MA 60m %')}
            </>
          )}
        </div>
        <div className="realtime-spread-pane-controls">
          <div className="realtime-spread-chart-time-filter">
            <label>
              <span>开始时间 UTC</span>
              <input
                type="datetime-local"
                value={startTime}
                min={startTimeBounds.min}
                max={startTimeBounds.max}
                onChange={onStartTimeChange}
                disabled={isChartLoading}
                title={`默认回看 ${formatLookbackDuration(intervalPreset.defaultMs)}，最多 ${formatLookbackDuration(intervalPreset.maxMs)}${
                  isTradePriceDataSource
                    ? ' · Trade 成交'
                    : isOppDetectorSource && isSpreadPriceMode
                      ? ' · L1 盘口'
                      : ''
                }`}
              />
            </label>
          </div>
          <div className="realtime-spread-interval-picker" role="group" aria-label="Interval">
            {INTERVAL_PRESETS.map((preset) => (
              <button
                key={preset.seconds}
                type="button"
                className={`realtime-spread-interval-btn${config.interval === preset.seconds ? ' is-active' : ''}`}
                aria-pressed={config.interval === preset.seconds}
                title={preset.label}
                onClick={() => selectInterval(preset.seconds)}
              >
                {preset.shortLabel}
              </button>
            ))}
          </div>
          <span className="realtime-spread-pane-controls-hint">子图分界可拖动调整高度 · 在「设置」中开关显示</span>
        </div>
        <div
          className={`realtime-spread-chart-shell${isChartLoading ? ' is-loading' : ''}`}
          style={{ height: chartViewportHeight }}
        >
          {isChartLoading ? (
            <div className="realtime-spread-chart-loading" role="status" aria-live="polite">
              <span className="realtime-spread-chart-loading-spinner" aria-hidden="true" />
              <span>加载 {config.symbol} 图表数据…</span>
            </div>
          ) : null}
          {isSpreadPriceMode ? (
            <div className="realtime-spread-chart-floating-controls">
              <button
                type="button"
                className="realtime-spread-chart-mode-toggle is-direction"
                title={`切换 Spread 方向（${config.direction}）`}
                onClick={toggleSpreadDirection}
              >
                方向: {spreadDirectionShortLabel(config.direction)}
              </button>
              <button
                type="button"
                className="realtime-spread-chart-mode-toggle is-kline-display"
                title="切换 K 线展示方式"
                onClick={toggleKlineDisplayMode}
              >
                {klineDisplayModeLabel(klineDisplayMode)}
              </button>
              {isOppDetectorSource ? (
                <>
              <button
                type="button"
                className="realtime-spread-chart-mode-toggle is-data-source"
                title="切换 Spread / K 线数据源"
                onClick={toggleOppPriceDataSource}
              >
                数据源: {oppPriceDataSourceLabel(oppPriceDataSource)}
              </button>
              {!isTradePriceDataSource ? (
                <button
                  type="button"
                  className="realtime-spread-chart-mode-toggle"
                  onClick={() => setSpreadViewMode((mode) => (mode === 'orderbook' ? 'mid' : 'orderbook'))}
                >
                  {detailedSpreadViewModeLabel(spreadViewMode)}
                </button>
              ) : null}
              {!isTradePriceDataSource ? (
                <div className="realtime-spread-chart-settings is-floating" ref={deviationCycleSettingsRef}>
                  {renderDeviationCycleSettingsPanel()}
                </div>
              ) : null}
                </>
              ) : null}
            </div>
          ) : null}
          {isSpreadPriceMode ? (
            <SpreadPricePaneChart
              chartHeight={chartViewportHeight}
              paneLayout={paneLayout}
              onPaneLayoutSnapshot={handlePaneLayoutSnapshot}
              showTradeVolumeStrip={isOppDetectorSource && !!inst1 && !!inst2}
              exchange1InstId={inst1}
              exchange2InstId={inst2}
              spreadViewMode={effectiveSpreadViewMode}
              openSpreadData={selectedOpenSpreadData}
              closeSpreadData={selectedCloseSpreadData}
              openLineData={selectedOpenLineData}
              closeLineData={selectedCloseLineData}
              midSpreadData={selectedMidSpreadData}
              medianMidSpreadData={selectedMedianMidSpreadData}
              direction={config.direction}
              seriesVisibility={seriesVisibility}
              deviationCycles={deviationCycles}
              intervalSeconds={config.interval}
              showDeviationCycles={seriesVisibility.deviationCycles}
              exchange1Data={exchange1KlineData}
              exchange2Data={exchange2KlineData}
              exchange1FundingData={exchange1FundingData}
              exchange2FundingData={exchange2FundingData}
              exchange1FundingSettlements={exchange1FundingSettlements}
              exchange2FundingSettlements={exchange2FundingSettlements}
              netFundingSettlementData={netFundingSettlementData}
              exchange1Label={config.exchange1}
              exchange2Label={config.exchange2}
              instructionThresholdLines={instructionThresholdLines}
              pairInstructions={pairInstructions}
              positionOpenSpreadLine={positionOpenSpreadLine}
              chartSpreadScale={chartSpreadScale}
              klineDisplayMode={klineDisplayMode}
              showFundingStrip={isSpreadPriceMode}
              fundingStrip={{
                exchange1: config.exchange1,
                exchange2: config.exchange2,
                direction: config.direction,
                fundingSummary,
                fundingInstrumentMeta,
                nextNetFunding,
                fundingNowMs,
                fundingSummaryUpdatedAtMs,
                fundingRefreshLoading,
                fundingRefreshError,
                onRefresh: () => void refreshFundingInfo(),
                refreshDisabled: !isOppDetectorSource || !inst1 || !inst2,
              }}
            />
          ) : (
            <RealtimeSpreadPaneChart
              chartHeight={chartViewportHeight}
              paneLayout={paneLayout}
              onPaneLayoutSnapshot={handlePaneLayoutSnapshot}
              openSpreadData={selectedOpenSpreadData}
              closeSpreadData={selectedCloseSpreadData}
              biasExchange1Data={biasExchange1Data}
              biasExchange2Data={biasExchange2Data}
              indexSpreadData={selectedIndexSpreadData}
              adjustedSpreadMaData={selectedAdjustedSpreadMaData}
              seriesVisibility={seriesVisibility}
              exchange1Data={exchange1KlineData}
              exchange2Data={exchange2KlineData}
              instructionThresholdLines={instructionThresholdLines}
              positionOpenSpreadLine={positionOpenSpreadLine}
              chartSpreadScale={chartSpreadScale}
            />
          )}
        </div>
        <VerticalResizeHandle
          value={chartViewportHeight}
          min={MIN_CHART_VIEWPORT_HEIGHT}
          max={MAX_CHART_VIEWPORT_HEIGHT}
          ariaLabel="调整图表区域高度"
          onChange={setChartViewportHeight}
          onCommit={(height) => saveChartViewportHeight(chartHeightStorageKey, height)}
        />
          </>
        ) : chartSectionTab === 'datadetail' ? (
          <div
            className="realtime-spread-chart-subpanel is-data-detail"
            style={{ minHeight: chartViewportHeight }}
          >
            <DataDetailPanel
              key={`${symbol}|${inst1}|${inst2}|${startTime}`}
              symbol={symbol}
              exchange1={config.exchange1}
              exchange2={config.exchange2}
              instId1={inst1}
              instId2={inst2}
              startTimeUtc={startTime}
            />
          </div>
        ) : (
          <div
            className="realtime-spread-chart-subpanel"
            style={{ height: chartViewportHeight }}
          >
            <BasicInfoPanel
              key={`${symbol}|${inst1}|${inst2}`}
              symbol={symbol}
              exchange1={config.exchange1}
              exchange2={config.exchange2}
              instId1={inst1}
              instId2={inst2}
              indexComponents1={indexComponentsMeta.exchange1}
              indexComponents2={indexComponentsMeta.exchange2}
            />
          </div>
        )}
      </section>
          </section>

          <RealtimeSpreadPositions
            selectedGroupId={selectedPositionGroupId}
            onSelectPositionGroup={applyPositionGroupChart}
            onChartLinkedGroupUpdate={handleChartLinkedGroupUpdate}
            onPositionGroupsUpdate={handlePositionGroupsUpdate}
            exchange1={config.exchange1}
            exchange2={config.exchange2}
            latestOpenLineByDirection={latestOpenLineByDirection}
            accountSummaryHost={accountSummaryHost}
          />
              </div>
            }
            right={
              <div className="realtime-spread-right-column">
                <div className="realtime-spread-trading-panel-wrap">
                  <ManualTradingPanel
                    symbol={symbol}
                    exchange1={config.exchange1}
                    exchange2={config.exchange2}
                    exchange1InstId={inst1}
                    exchange2InstId={inst2}
                    realtimeDirection={config.direction}
                    latestOpenLine={latestOpenLineForDirection}
                  />
                </div>
                <div
                  ref={setAccountSummaryHost}
                  className="realtime-spread-account-sidebar-host"
                  aria-label="账户概览"
                />
                <section className="realtime-spread-events-sidebar-host" aria-label="执行事件">
                  <TradingEventLog />
                </section>
              </div>
            }
          />
        </div>
        </TradeVolumeProvider>
      </div>
    </div>
  );
}
